/*
 * Boston Proper × Bloomreach Discovery client.
 *
 * Takes over the PLP grid, filter drawer, sort, and Load More on
 * collections.collection pages when the merchant flips
 * data-br-enabled="1" on <bp-collection>.
 *
 * The Liquid markup (Shopify collection.products + collection.filters)
 * stays in place as a no-JS / dev-preview / CORS fallback. When BR is
 * enabled and reachable, this script swaps in BR-driven data on top.
 *
 * Public surface:
 *   window.BpBloomreach.init(rootEl, config)
 *
 * URL state is Shopify-style (filter.v.option.color=…, sort_by=…,
 * page=…) so:
 *   1. Links remain shareable + parseable by the rest of the theme.
 *   2. If BR is later disabled, the same URLs still work against
 *      Shopify's native filters.
 *
 * Card HTML is intentionally a slim variant of bp-product-card.liquid
 * (matches the same .bp-card / .bp-coll__cell classes so existing CSS
 * applies). It links to the PDP rather than opening the quick-add
 * drawer, because the drawer needs a full Shopify product context that
 * BR doesn't provide. Drawer integration is a v2 follow-up.
 */

(function () {
  "use strict";

  // Default endpoint — production. Staging callers should override
  // via cfg.host (set by `data-br-host` on <bp-collection>, which
  // reads from `window.shop.br.host` in bp-shop-globals.liquid).
  // BR docs: prod = `core.dxpapi.com`, stg = `staging-core.dxpapi.com`.
  const DEFAULT_ENDPOINT = "https://core.dxpapi.com/api/v1/core/";
  // `sizes` is intentionally NOT requested: BR flagged it as payload/latency
  // weight on category calls and it's never rendered here. Size *filtering*
  // comes from BR's facet response, not fl.
  // `colors` IS requested: it drives the initial card swatches AND the
  // colourway-name strip on the card title (currentColor() matches a family
  // colour against the handle suffix — with no colours it silently no-ops and
  // the "…- Safari Brown" suffix leaks into the PLP/search card name).
  const FIELD_LIST = "pid,title,price,sale_price,thumb_image,url,brand";
  // Keyword-search field list: same, minus `colors` — BR's keyword feed
  // doesn't back that field, so it's not requested on the /search PLP.
  const FIELD_LIST_SEARCH = "pid,title,price,sale_price,thumb_image,url,brand";

  // §9.4 product-card knobs, set from the section's data-attrs in init().
  // renderCard() is a bare map() callback (no cfg arg), so it reads these.
  // The quick-add markup itself is injected lazily by bp-card-hydrate.js;
  // here we only emit the bag trigger + the data-attrs its click handler
  // (bp-collection.liquid bindBagIcons) and the visibility CSS key off.
  const CARD_CFG = {
    qbStyle: "button", // button | whole_product | disabled
    qvDest: "drawer", // drawer | pdp
    qvVisD: "hover", // hover | always
    qvVisM: "shown", // shown | hidden
    // Secondary-image-on-hover — BR's feed carries only one image
    // (thumb_image), so the actual <img class="bp-card__image--secondary">
    // is injected later by assets/bp-card-hydrate.js once it resolves the
    // product's style code. This flag just decides whether the card is
    // marked eligible for it.
    secondaryHover: true,
  };

  // Source collection handle for the current PLP — set from cfg in init().
  // When present, renderCard namespaces product links as
  // /collections/{handle}/products/{handle} so the PDP's `collection` global
  // is set and its breadcrumb shows where the customer came from (Atlas
  // parity). Empty on the /search PLP → plain /products/{handle} links.
  let BR_COLLECTION_HANDLE = "";

  // Bag icon — same glyph as snippets/bp-product-card.liquid so BR cards
  // match the Liquid cards. Styled globally via .bp-card__quick in
  // snippets/bp-fonts.liquid.
  const BAG_SVG =
    '<svg viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M4.18929 5.74434H16.5343H16.6292L16.6729 5.74549C16.9007 5.75099 17.1328 5.87803 17.2448 6.08089C17.3145 6.20157 17.3382 6.30372 17.3547 6.43048C17.6354 9.18806 17.9008 11.996 18.3051 14.7356C18.3771 15.2467 18.4466 15.758 18.5137 16.2697C18.5163 16.2917 18.5195 16.336 18.5207 16.3582C18.5378 16.6193 18.4156 16.9263 18.1508 17.0687C17.9651 17.1752 17.7807 17.1763 17.5845 17.1755C16.1978 17.1755 4.69428 17.1755 3.34023 17.1755C3.20422 17.1726 3.01496 17.1862 2.86505 17.1451C2.45036 17.0406 2.25213 16.656 2.31666 16.2445C2.41071 15.5271 2.58579 14.2489 2.68621 13.5185C2.95476 11.5399 3.15849 9.46674 3.35962 7.47459C3.37582 7.3571 3.44296 6.63739 3.45714 6.52887L3.46698 6.43424L3.46814 6.42237C3.46814 6.41948 3.46901 6.4134 3.47045 6.40559C3.5156 6.02417 3.79254 5.74028 4.18929 5.74434ZM4.18929 6.6125C4.2136 6.6125 4.24804 6.59601 4.27872 6.57343C4.30302 6.55491 4.3201 6.53089 4.32704 6.51845C4.33457 6.50601 4.33486 6.50138 4.33399 6.50427C4.33341 6.50803 4.33225 6.51353 4.33138 6.51758C4.33138 6.51758 4.33081 6.51932 4.33052 6.52424L4.32936 6.5361L4.31923 6.63044C4.132 8.4151 3.9054 10.3181 3.74624 12.1016C3.63685 13.4481 3.56103 15.0256 3.46727 16.3753L3.46582 16.3915V16.3961C3.46582 16.3961 3.46611 16.3973 3.4664 16.3915C3.4664 16.3872 3.4664 16.375 3.46351 16.3588C3.45396 16.2908 3.41721 16.2161 3.3602 16.1507C3.29306 16.0798 3.20422 16.0246 3.12319 16.0162C3.11856 16.0162 3.13361 16.0173 3.15647 16.0176C4.67199 16.0182 16.0117 16.0176 17.5848 16.0176H17.6719C17.6823 16.017 17.6916 16.0168 17.6962 16.0165C17.7014 16.0162 17.7034 16.0159 17.6991 16.0162C17.5752 16.0286 17.4268 16.1591 17.3857 16.2795C17.3576 16.3556 17.3617 16.3788 17.362 16.3941C17.362 16.3921 17.362 16.393 17.362 16.3886L17.3605 16.3724C17.254 14.8543 17.1872 13.3292 17.0509 11.8148C16.8906 10.0895 16.6752 8.23163 16.4883 6.50456C16.495 6.52019 16.5161 6.55433 16.5468 6.57575C16.5714 6.59485 16.5989 6.60585 16.6145 6.60961L16.6243 6.61192H16.6284C16.6284 6.61192 16.6298 6.61279 16.6304 6.61279C16.6318 6.61279 16.6336 6.61279 16.633 6.61279C16.6324 6.61279 16.635 6.61279 16.6292 6.61279C16.5783 6.61221 16.4 6.61337 16.3445 6.61279H4.18929V6.6125Z"/>' +
    '<path d="M6.08301 8.78064C6.1788 7.5189 6.20658 6.21405 6.58539 4.98907C7.15432 3.11007 8.4421 2.11631 10.414 2.12673C13.8001 2.13338 14.5031 4.87997 14.6721 7.71395C14.6964 8.07453 14.7158 8.43453 14.745 8.78035L13.5874 8.77485C13.6172 8.46057 13.6575 8.02649 13.6754 7.71858C13.801 5.83351 13.5976 3.34129 11.3988 2.81605C10.7575 2.6676 10.0702 2.6676 9.42892 2.81605C7.61503 3.24811 7.15143 5.0374 7.12683 6.6872C7.11121 7.36929 7.17545 8.09132 7.24056 8.77485L6.08301 8.78035V8.78064Z"/>' +
    "</svg>";

  // Shopify sort_by → BR sort. Empty string means "let BR default rank".
  const SORT_MAP = {
    manual: "",
    "best-selling": "", // no native BR equivalent; fall back to default rank
    // Sort by sale_price (the effective selling price the customer pays and
    // that the card displays) — BR sets sale_price == price for non-sale
    // items, so this orders on-sale products by their discounted price, not
    // the regular price. Sorting on `price` (list) put a $325→$279 item above
    // a $250 item on High→Low even though the shopper pays less (QA 7/21).
    "price-ascending": "sale_price asc",
    "price-descending": "sale_price desc",
    "created-ascending": "launch_date asc",
    "created-descending": "launch_date desc",
    "title-ascending": "title asc",
    "title-descending": "title desc",
  };

  // Shopify filter param key → BR facet field name.
  // The drawer renders pills with data-param="<shopify key>" so existing
  // markup keeps working unchanged.
  const FILTER_MAP = {
    "filter.v.option.color": "colors",
    "filter.v.option.colour": "colors",
    "filter.v.option.size": "sizes",
    "filter.p.vendor": "brand",
  };

  // (The reverse lookup that used to live here is now prettyParamFor(),
  // below — it normalizes the facet name first, so it keeps working when
  // BR renames `colors` to `COLOR`.)

  // Human labels for BR facet names. BR's facet "name" field is a slug;
  // we want capitalized labels for the drawer accordion headings.
  // Keyed by NORMALIZED name — see normFacet.
  const FACET_LABELS = {
    color: "Color",
    colors: "Color",
    size: "Size",
    sizes: "Size",
    brand: "Brand",
    category: "Category",
    color_groups: "Color Group",
    // Custom BP attribute facets (BR "Attribute name"s, normalized to
    // lowercase — see normFacet). Without these, labelFor() would render
    // "Bp Sleevelength".
    // NOTE: no `producttype` label on purpose — BR already surfaces the
    // productType attribute AS the CATEGORY facet (display name "CATEGORY",
    // values like "Dresses"), which is the working Category filter. Labeling
    // productType separately would risk a duplicate "Product Type" filter
    // showing the same values as Category. If a genuinely distinct Product
    // Type filter is ever wanted, it needs a different underlying BR attribute.
    bp_sleevelength: "Sleeve Length",
    bp_silhouette: "Silhouette",
    bp_neckline: "Neckline",
    occasion: "Occasion",
  };

  // Which facets BR returns is owned by BR (Algo defaults + merchandiser
  // pins in the Discovery dashboard), NOT requested by the theme. We used to
  // append `facet.field` for BP_Sleevelength/BP_Silhouette/BP_Neckline/
  // Occasion, but per BR guidance the theme should not hard-code facet fields
  // in the request — the drawer renders whatever BR returns.

  // BR renames facets server-side without warning — this catalog served
  // `colors`/`sizes`/`brand`/`price` earlier and `COLOR`/`SIZE`/`BRAND`/
  // `PRICE` later the same day. Every lookup that keys off a facet name
  // therefore normalizes first; exact-match lookups silently broke the
  // drawer (an unlabeled PRICE facet reappeared, and CATEGORY rendered
  // pills that filtered nothing).
  //
  // The one thing that must NOT be normalized is the field used in `fq`:
  // BR accepts `COLOR:"black"` and legacy `colors:"black"` (both 228
  // results) but silently returns 0 for a guessed `color:"black"`. So the
  // query always uses the facet name exactly as BR returned it.
  function normFacet(name) {
    return String(name == null ? "" : name)
      .trim()
      .toLowerCase();
  }

  // BR facet name → Shopify-style URL param, for shareable/pretty URLs.
  // Returns null for facets with no Shopify equivalent; those round-trip
  // through `filter.br.<FacetName>` instead.
  function prettyParamFor(facetName) {
    const n = normFacet(facetName);
    if (n === "color" || n === "colors") return "filter.v.option.color";
    if (n === "size" || n === "sizes") return "filter.v.option.size";
    if (n === "brand") return "filter.p.vendor";
    return null;
  }

  // Drawer ordering rank. Normalized so a BR rename can't reshuffle it.
  function facetRank(facetName) {
    const n = normFacet(facetName);
    if (n === "color" || n === "colors") return 0;
    if (n === "size" || n === "sizes") return 1;
    if (n === "brand") return 2;
    return 3;
  }

  function labelFor(facetName) {
    const n = normFacet(facetName);
    if (FACET_LABELS[n]) return FACET_LABELS[n];
    // "PRODUCT_TYPE" → "Product Type" rather than a shouty "PRODUCT_TYPE".
    return String(facetName)
      .replace(/[_-]+/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  // Active-value lookup that tolerates the state key and the facet name
  // disagreeing in case/plurality — state can be hydrated from a URL
  // (`filter.v.option.color` → `colors`) before BR reports `COLOR`.
  function activeValuesFor(activeFilters, facetName) {
    const n = normFacet(facetName);
    const keys = Object.keys(activeFilters || {});
    for (const k of keys) {
      if (normFacet(k) === n) return activeFilters[k] || [];
    }
    const ALIAS = {
      color: "colors",
      colors: "color",
      size: "sizes",
      sizes: "size",
    };
    const alias = ALIAS[n];
    if (alias) {
      for (const k of keys) {
        if (normFacet(k) === alias) return activeFilters[k] || [];
      }
    }
    return [];
  }

  const BR_PARAM_PREFIX = "filter.br.";

  const MONEY = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  function money(n) {
    if (n == null || isNaN(n)) return "";
    // Strip whole-dollar cents ($70.00 → $70) but keep real cents ($70.50),
    // matching Atlas + the rest of the site.
    return MONEY.format(Number(n)).replace(/\.00$/, "");
  }

  function escapeHtml(s) {
    if (s == null) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // BR `thumb_image` is a RAW Shopify CDN URL with no resize — a ~250KB
  // original painted into a ~175-360px card slot (×24 ≈ 6MB/page). Append
  // Shopify's `width` param so the CDN serves a card-sized image (and
  // auto-negotiates webp/avif via Accept). Only rewrites Shopify CDN URLs;
  // Amplience / other hosts are left untouched.
  function sizedImg(url, w) {
    if (!url || !/cdn\.shopify\.com|\/cdn\/shop\//.test(url)) return url;
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + w;
  }

  // Amplience "set" service image, addressed by INDEX within the style's set
  // (_SET/1 = 1st real image, _SET/2 = 2nd, …), card-sized. This is what Atlas
  // uses for PLP cards (collection.js `_SET/1` primary + `_SET/2` hover) and it
  // is the fix for the grey-background first image on accessories/shoes/jewelry
  // (QA 7/27, Brooke): BR's `thumb_image` is Shopify's `{style}_SET_….jpg`
  // COMPOSITE — a grey-bg flat for products that lack a `_1` ghost — whereas the
  // set service returns only images that actually exist, so `_SET/1` is always
  // the true first image regardless of which suffix it lives under. `fmt=auto`
  // lets Amplience negotiate webp/avif via Accept.
  function ampSetImg(style, index, w) {
    const amp = (window.bpColorSiblings && window.bpColorSiblings.amplience) || "";
    if (!amp || !style) return "";
    return amp + "/s/bostonproper/" + style + "_SET/" + index + "?w=" + w + "&fmt=auto";
  }

  // Extract the Shopify product handle from a BR product URL like
  // "https://www.bostonproper.com/products/<handle>". Falls back to
  // empty string so the card still renders.
  function handleFromUrl(url) {
    const m = String(url || "").match(/\/products\/([^/?#]+)/);
    return m ? m[1] : "";
  }

  // BR's product docs carry no `sku` field (and empty `variants`), but every
  // BP image asset is named `<SKU>_SET…` (e.g. `2CY9430_SET_….jpg`), and that
  // leading token IS the inv7 SKU the GA4 items[] needs (item_id = inv7,
  // item_parent_id = inv5, item_variant = full SKU). Pull it from thumb_image
  // and validate the inv7 shape (digit + 2 letters + 4 digits) so a
  // non-conforming marketplace asset falls back to "" instead of a garbage id.
  function skuFromImage(url) {
    // Take the FILENAME (last path segment) — the Shopify CDN path has two
    // `/files/` segments (…/s/files/1/0529/…/files/<name>), so a
    // `/files/([^/]+)/` match grabs the account id, not the asset name.
    const clean = String(url || "").split(/[?#]/)[0];
    const file = clean.slice(clean.lastIndexOf("/") + 1);
    const token = file.split(/[_.]/)[0].toUpperCase();
    return /^\d[A-Z]{2}\d{4}$/.test(token) ? token : "";
  }

  // Convert a BR doc → the shape our card renderer expects.
  function normalizeDoc(doc) {
    const handle = handleFromUrl(doc.url);
    const sale = doc.sale_price != null ? Number(doc.sale_price) : null;
    const list = doc.price != null ? Number(doc.price) : null;
    // A real money value is a positive number. Some feed items (e.g. Hanky
    // Panky) carry a 0 / blank sale_price or price; 0 is NOT a real sale
    // and must never surface as "$0.00" or a bogus strikethrough (QA:
    // Brooke). Validate before deciding sale state.
    const validSale = sale != null && !isNaN(sale) && sale > 0;
    const validList = list != null && !isNaN(list) && list > 0;
    // BR convention: sale_price equals price when not on sale. On sale only
    // when BOTH are valid positives and the sale is strictly lower.
    const onSale = validSale && validList && sale < list - 0.001;
    // Displayed price is always the real selling price — never 0/blank.
    const priceOut = onSale ? sale : validList ? list : validSale ? sale : null;
    return {
      pid: doc.pid,
      handle,
      // Prefer the handle-based relative path over BR's absolute URL.
      // BR staging returns `https://staging.example.com/products/<handle>`
      // (placeholder host), and prod may return
      // `https://www.bostonproper.com/products/<handle>` which is wrong
      // when the customer is on a non-www variant. A relative
      // `/products/<handle>` resolves against the current origin
      // (staging.bostonproper.com or bostonproper.com) so links always
      // navigate to the current shop.
      url: handle
        ? BR_COLLECTION_HANDLE
          ? `/collections/${BR_COLLECTION_HANDLE}/products/${handle}`
          : `/products/${handle}`
        : doc.url || "#",
      title: doc.title || "",
      image: doc.thumb_image || "",
      price: priceOut,
      compare_at_price: onSale ? list : null,
      brand: doc.brand || "",
      // No `sku` in BR docs — derive the inv7 SKU from the image filename.
      sku: doc.sku || skuFromImage(doc.thumb_image),
      // Dedup color list (BR returns one entry per variant). Dedup by the
      // HANDLEIZED name, not the raw string — variants like "Blue White" and
      // "Blue/White" are the same colourway (both → the same sibling handle
      // `…-blue-white`) and were rendering as two identical swatches (QA:
      // Tamara). `sizes` is not requested in fl (see FIELD_LIST) — nothing
      // renders it.
      colors: dedupeColors(doc.colors),
    };
  }

  // Collapse colour names that resolve to the same handle (e.g. "Blue White"
  // vs "Blue/White"), keeping the first-seen display value.
  function dedupeColors(colors) {
    if (!Array.isArray(colors)) return [];
    const seen = {};
    const out = [];
    colors.forEach(function (c) {
      const key = handleize(c);
      if (!key || seen[key]) return;
      seen[key] = 1;
      out.push(c);
    });
    return out;
  }

  // Coerce a free-form color name (e.g. "Safari Brown") into a value
  // safe to interpolate inside `background-color: …`. Strips anything
  // that isn't a letter → no CSS injection vector. Falls back to the
  // browser's "invalid color = inherited" behavior when the result
  // isn't a real CSS color keyword (same UX the filter drawer ships).
  function colorToCss(name) {
    return String(name)
      .toLowerCase()
      .replace(/[^a-z]/g, "");
  }

  // Colour-facet swatch background. Prefer the merchant `product_color_swatches`
  // metaobject map (window.bpColorSwatches, name -> hex OR a full CSS gradient,
  // loaded in bp-shop-globals.liquid) so custom names like Metallic / Multi /
  // Neutral / Off White aren't empty circles. Falls back to the raw CSS colour
  // name (valid keywords like "red"/"navy" still work); returns "" when neither
  // resolves. Applied via `background:` so a gradient value renders.
  function swatchBackground(name) {
    var mapped =
      typeof window !== "undefined" && window.bpColorSwatches
        ? window.bpColorSwatches.get(name)
        : "";
    return mapped || colorToCss(name);
  }

  // Shopify-style handleize: lowercase, non-alphanumeric runs -> '-', trim.
  function handleize(s) {
    return String(s == null ? "" : s)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  // Derive the master (colour-family base) handle from a colorway product's URL
  // + its family colour names: the handle is `${master}-${currentColorHandle}`,
  // so find the family colour whose handle is the URL's suffix and strip it.
  // Returns '' when nothing matches, in which case the caller omits the batch
  // attrs and the card falls back to the per-card ?view=hydrate path.
  function deriveMaster(url, colors) {
    const handle = handleFromUrl(url);
    if (!handle || !colors || !colors.length) return "";
    for (let i = 0; i < colors.length; i++) {
      const ch = handleize(colors[i]);
      if (
        ch &&
        handle.length > ch.length + 1 &&
        handle.slice(-(ch.length + 1)) === "-" + ch
      ) {
        return handle.slice(0, handle.length - ch.length - 1);
      }
    }
    return "";
  }

  // Return the display color name for THIS colorway card: the family
  // colour whose handleized form is the URL handle's suffix (same match
  // deriveMaster uses, but returning the human-readable colour string).
  // Empty when nothing matches. Used to strip the colour off the visible
  // product name so cards read "Marilyn Cowl-Neck Charmeuse Blouse", not
  // "… - Safari Brown" (Atlas parity — Atlas titles carry no colour).
  function currentColor(url, colors) {
    const handle = handleFromUrl(url);
    if (!handle || !colors || !colors.length) return "";
    for (let i = 0; i < colors.length; i++) {
      const ch = handleize(colors[i]);
      if (
        ch &&
        handle.length > ch.length + 1 &&
        handle.slice(-(ch.length + 1)) === "-" + ch
      ) {
        return colors[i];
      }
    }
    return "";
  }

  // Strip the trailing colourway off a product title using the KNOWN
  // colour for the card, case-insensitive. The separator is optional —
  // BP titles append the colour either " - Safari Brown" or plain
  // " Sesame" — so the dash is optional and only the colour anchors the
  // match at end-of-string. Keying off the exact known colour means legit
  // internal words/hyphens ("Cowl-Neck", "Medium Wash" base names) are
  // safe. No-op when color is empty, the title doesn't end with it, or
  // stripping would empty the name.
  function stripColorSuffix(title, color) {
    if (!title || !color) return title;
    const c = String(color).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp("\\s*[-\\u2013\\u2014]?\\s*" + c + "\\s*$", "i");
    const out = String(title).replace(re, "").trim();
    return out || title;
  }

  // Match the Liquid card / hydrate limit (3) so the initial BR render has
  // the same shape as the hydrated result — less layout shift. Bloomreach's
  // feed carries no per-colour availability, so this first paint counts ALL
  // colour names; bp-card-hydrate.js then replaces these with the in-stock
  // only set + exact +N from the single-product hydrate endpoint.
  const SWATCH_LIMIT = 3;

  // Build the swatch <ul>. Colors are already deduped in normalizeDoc;
  // limit to SWATCH_LIMIT and show "+N" overflow chip when more exist.
  function renderSwatches(colors) {
    if (!colors || !colors.length) return "";
    const shown = colors.slice(0, SWATCH_LIMIT);
    const items = shown
      .map(
        (c) => `
      <li class="bp-card__swatch-item">
        <span class="bp-card__swatch-img"
              style="background-color: ${escapeHtml(colorToCss(c))};"
              role="img"
              aria-label="${escapeHtml(c)}"
              title="${escapeHtml(c)}"></span>
      </li>
    `,
      )
      .join("");
    const overflow =
      colors.length > SWATCH_LIMIT
        ? `<li class="bp-card__swatch-more" aria-hidden="true">+${colors.length - SWATCH_LIMIT}</li>`
        : "";
    return `<ul class="bp-card__swatches" role="list" aria-label="Available colors">${items}${overflow}</ul>`;
  }

  function renderCard(p, eager) {
    // Defensive: only strike a compare-at price that is a real number
    // strictly above the selling price. Guards against a 0 / blank / ≤price
    // compare ever rendering "$0.00" or a fake sale (QA: Brooke).
    const cmp = Number(p.compare_at_price);
    const compare =
      p.compare_at_price != null && !isNaN(cmp) && cmp > Number(p.price)
        ? `<s class="bp-card__compare">${escapeHtml(money(cmp))}</s>`
        : "";
    // data-bp-card-image-primary is the hook the global swatch handler
    // (snippets/bp-card-swatches.liquid) looks the card image up by —
    // without it a swatch click updates the active dot, price, and
    // links but silently leaves the image on the old color, because
    // `card.querySelector('[data-bp-card-image-primary]')` finds
    // nothing. snippets/bp-product-card.liquid emits the same hook.
    //
    // Card-sized responsive image (see sizedImg). Must match the Liquid card
    // (snippets/bp-product-card.liquid) for parity — it offers up to 1080w
    // with sizes "(min-width:1024px) 25vw, 50vw". The old BR card capped at
    // 540w with a fixed 260px hint, so on desktop (cards are wider than 260px)
    // and on any 2× retina screen the browser upscaled 540w → blurry (QA
    // 7/24). The 360/540 rungs still serve small on mobile / 1× so load stays
    // light; the browser only fetches 720/1080 when the display actually needs
    // it. `eager` first-screen cards load immediately + high priority (LCP);
    // the rest stay lazy.
    // Atlas parity: draw the primary from Amplience's set service (_SET/1) so
    // accessories/shoes/jewelry get their TRUE first image instead of the
    // grey-bg Shopify `_SET` composite thumb (see ampSetImg). The discriminator
    // is the STYLE TOKEN, not the brand: `p.sku` is skuFromImage(thumb_image),
    // which only returns a value when the filename is inv7-shaped
    // (`{digit}{2 letters}{4 digits}_SET_….jpg`) — the exact naming BP's
    // Amplience-photographed catalog uses, INCLUDING branded goods BP stocks
    // and shoots (Andre Assous, Sam Edelman, BCBGeneration). Do NOT gate on
    // brand ≠ "Boston Proper": that wrongly excluded those branded products and
    // left them on the grey composite (QA 7/27, Brooke — `2DH4390`). True
    // Shopify Collective / marketplace items use the vendor's own image URLs
    // (e.g. `Ettika64ae….jpg`, `6079-taylor-….jpg`), which aren't inv7-shaped,
    // so skuFromImage returns "" and they correctly fall back to the Shopify
    // thumb.
    const ampStyle = p.sku;
    const ampPrimary1080 = ampSetImg(ampStyle, 1, 1080);
    let img360, img540, img720, img1080;
    if (ampPrimary1080) {
      img360 = escapeHtml(ampSetImg(ampStyle, 1, 360));
      img540 = escapeHtml(ampSetImg(ampStyle, 1, 540));
      img720 = escapeHtml(ampSetImg(ampStyle, 1, 720));
      img1080 = escapeHtml(ampPrimary1080);
    } else {
      img360 = escapeHtml(sizedImg(p.image, 360));
      img540 = escapeHtml(sizedImg(p.image, 540));
      img720 = escapeHtml(sizedImg(p.image, 720));
      img1080 = escapeHtml(sizedImg(p.image, 1080));
    }
    const loadAttrs = eager
      ? 'loading="eager" fetchpriority="high"'
      : 'loading="lazy"';
    // Secondary (hover) image, rendered INLINE for house products so the hover
    // is correct on paint, not dependent on bp-card-hydrate.js running. Primary
    // is `_SET/1` (items[0]); hover must be `_SET/2` (items[1]) — the NEXT real
    // image. The old hover was `/i/{style}_2`, which on accessories/shoes IS the
    // first real image (no `_1`), so it equalled the new primary and hover
    // showed no change — "first image same as hover" (QA 7/27, Brooke). Mirrors
    // Atlas's PLP `_SET/2` hover and the applySecondaryImage() DOM contract
    // (class/alt/aria/hook) so hydration's guard skips it. Only when the
    // secondary-hover knob is on and the product resolves to an Amplience style;
    // Collective items fall through to hydration's Shopify hover.
    const ampHover720 = CARD_CFG.secondaryHover ? ampSetImg(ampStyle, 2, 720) : "";
    const secondaryImg = ampHover720
      ? `<img class="bp-card__image bp-card__image--secondary"
              src="${escapeHtml(ampHover720)}"
              alt=""
              loading="lazy"
              aria-hidden="true"
              data-bp-card-image-secondary>`
      : "";
    const image = img720
      ? `<img class="bp-card__image bp-card__image--primary"
              src="${img720}"
              srcset="${img360} 360w, ${img540} 540w, ${img720} 720w, ${img1080} 1080w"
              sizes="(min-width: 1024px) 25vw, 50vw"
              alt="${escapeHtml(p.title)}"
              ${loadAttrs}
              data-bp-card-image-primary>${secondaryImg}`
      : "";
    const bag =
      CARD_CFG.qbStyle === "button"
        ? `<button type="button" class="bp-card__quick"
                 aria-label="Quick add ${escapeHtml(p.title)}"
                 data-product-id="${escapeHtml(p.pid)}"
                 data-bp-card-qv-trigger>${BAG_SVG}</button>`
        : "";
    // Brand eyebrow above the title — same "BRANDS WE LOVE: <brand>" line
    // Atlas shows for collective items. The BR feed carries `brand`
    // (= Shopify vendor); vendor is "Boston Proper" on house products, so
    // only external brands (BWL Marketplace) get the eyebrow — the same
    // `brand !== "Boston Proper"` collective test Atlas uses. Rendered with
    // the Liquid card's `.bp-card__collection` class (uppercased in CSS).
    const brand = (p.brand || "").trim();
    const eyebrow =
      brand && brand.toLowerCase() !== "boston proper"
        ? `<p class="bp-card__collection">Brands We Love: ${escapeHtml(brand)}</p>`
        : "";
    // Batch-hydrate hooks: expose the colour family so bp-card-hydrate.js can
    // resolve every colour's swatch in ONE Storefront-GraphQL call instead of a
    // per-card ?view=hydrate fetch. Only when the master handle is derivable;
    // otherwise omit them and the card falls back to the per-card path.
    const master = deriveMaster(p.url, p.colors);
    // Visible name only: drop the colourway suffix (Atlas parity). alt /
    // aria-label / GA4 trackJson keep the full p.title on purpose — Atlas
    // keeps the colour in the image alt, and analytics/a11y colour context
    // must not change.
    const cardName = stripColorSuffix(p.title, currentColor(p.url, p.colors));
    const batchAttrs = master
      ? ` data-bp-card-master="${escapeHtml(master)}" data-bp-card-colors="${escapeHtml(p.colors.join(","))}"`
      : "";
    // GA4 tracking payload — same shape + attribute as the Liquid card
    // (snippets/bp-product-card.liquid), so bp-track-cards.js fires
    // select_item + Quickview view_item on BR-rendered PLP cards too. BR's
    // FIELD_LIST only returns pid/title/price/brand/colors/sku, so the
    // variant is partial (no id/size/live inventory) — enough for item_id
    // (inv7 SKU), name, price, list + index attribution. escapeHtml keeps
    // the JSON valid inside the double-quoted attribute (the DOM decodes it
    // back for JSON.parse).
    const trackJson = escapeHtml(
      JSON.stringify({
        id: p.pid,
        title: p.title,
        vendor: p.brand || "Boston Proper",
        handle: p.handle,
        url: p.url,
        image: sizedImg(p.image, 720),
        category: "",
        product_type: "",
        // Item-level SKU (BR feed's product `sku`, one value per product —
        // NOT a per-variant SKU). GA4 items[] derives item_id/item_variant/
        // item_parent_id from it. Lives on the product, not selected_variant.
        sku: p.sku || "",
        selected_variant: {
          id: null,
          price: p.price,
          compare_at_price: p.compare_at_price,
          option1: (p.colors && p.colors[0]) || "",
          option2: "",
          product_id: p.pid,
          available: true,
          inventory_quantity: null,
        },
      }),
    );
    return `
      <li class="bp-coll__cell">
        <product-card class="bp-card-host">
          <article class="bp-card"
                   data-product-id="${escapeHtml(p.pid)}"
                   data-product-url="${escapeHtml(p.url)}"
                   data-bp-card-qb-style="${escapeHtml(CARD_CFG.qbStyle)}"
                   data-bp-card-qv-dest="${escapeHtml(CARD_CFG.qvDest)}"
                   data-bp-card-qv-vis-d="${escapeHtml(CARD_CFG.qvVisD)}"
                   data-bp-card-qv-vis-m="${escapeHtml(CARD_CFG.qvVisM)}"
                   data-bp-card-secondary-hover="${CARD_CFG.secondaryHover}"
                   data-bp-track-product="${trackJson}"
                   data-bp-source="bloomreach"${batchAttrs}>
            <div class="bp-card__media">
              <a class="bp-card__media-link"
                 href="${escapeHtml(p.url)}"
                 aria-label="${escapeHtml(p.title)}"
                 ref="productCardLink">${image}</a>
              <div class="bp-card__badges" data-bp-card-badges></div>
              ${bag}
            </div>
            <div class="bp-card__body">
              ${eyebrow}
              <h3 class="bp-card__title">
                <a href="${escapeHtml(p.url)}">${escapeHtml(cardName)}</a>
              </h3>
              <div class="bp-card__price-row">
                <span class="bp-card__price">${escapeHtml(money(p.price))}</span>
                ${compare}
              </div>
              ${renderSwatches(p.colors)}
            </div>
          </article>
        </product-card>
      </li>
    `;
  }

  // Quote a BR facet value: per BR's faceting docs, single values are
  // passed as `colors:"red"`. Multi-value within one facet uses
  // `colors:"red" OR "purple"`.
  function quote(s) {
    return '"' + String(s).replace(/(["\\])/g, "\\$1") + '"';
  }

  // Build a Discovery API URL from current state.
  function buildRequestUrl(cfg, state) {
    const params = new URLSearchParams();
    params.set("account_id", cfg.accountId);
    params.set("domain_key", cfg.domainKey);
    params.set("request_type", "search");
    // Keyword mode (the /search PLP) vs category mode (collection PLP).
    // When a search query is set, run BR keyword search; otherwise the
    // Bloomreach category takeover for a collection. Everything else —
    // facets, filters, sort, pagination, cards — is identical.
    if (state.searchQuery) {
      params.set("search_type", "keyword");
      params.set("q", state.searchQuery);
    } else {
      params.set("search_type", "category");
      params.set("q", state.catId);
    }
    params.set("rows", String(state.rows));
    params.set("start", String(state.start));
    // `colors` is requested for the category (collection) takeover — it drives
    // the card swatches + the colourway-name strip. It is NOT requested for
    // keyword search: BR's keyword feed doesn't back it, so asking for it there
    // is dropped/unhelpful. Keep the search fl to the fields BR search returns.
    params.set("fl", state.searchQuery ? FIELD_LIST_SEARCH : FIELD_LIST);
    params.set("url", cfg.siteUrl + state.path);
    // Only send a real BR cookie value. Never fabricate uid=0:v=app:ts=0:hc=1
    // — BR flags that sentinel as an invalid _br_uid_2 (it pollutes
    // personalization/reporting). When the cookie is absent the param is
    // omitted and BR treats the call as anonymous.
    if (cfg.brUid) params.set("_br_uid_2", cfg.brUid);
    // auth_key: BR's implementation audit asks for it on all Discovery calls.
    // Sent whenever configured (cfg.authKey ← window.shop.br.authKey ←
    // settings.bp_br_auth_key); omitted while the setting is blank so we never
    // send an empty value.
    if (cfg.authKey) params.set("auth_key", cfg.authKey);
    if (cfg.refUrl) params.set("ref_url", cfg.refUrl);

    // Content injection — ask BR to interleave merchandised media/promo
    // tiles (and STL triggers) into response.docs at their slots. Gated so
    // a surface can opt out. renderGrid branches on docType to render them.
    if (cfg.contentInjection) params.set("content_injection", "true");

    // Merchandising blend selector. Bloomreach Discovery scores results
    // against a configured ranking blend ("All PLP default" / "Sale &
    // Clearance" / "Clearance Only" etc., each with its own weight
    // distribution). When `view_id` is set the request runs against
    // that specific blend; when unset BR uses the account's default
    // blend so every existing surface keeps its prior ranking until a
    // blend is explicitly assigned per surface.
    if (cfg.viewId) params.set("view_id", cfg.viewId);

    // Sort.
    if (state.sort) params.set("sort", state.sort);

    // Facet filters. Each facet's multi-value list becomes one fq with
    // OR between values; multiple facets become multiple fq params with
    // AND between them.
    Object.keys(state.filters).forEach((facetField) => {
      const vals = state.filters[facetField];
      if (!vals || !vals.length) return;
      const fqValue = facetField + ":" + vals.map(quote).join(" OR ");
      params.append("fq", fqValue);
    });

    // Price range. Shopify uses gte/lte (in dollars).
    const lo = state.priceMin;
    const hi = state.priceMax;
    if (lo != null || hi != null) {
      const a = lo != null ? lo : "*";
      const b = hi != null ? hi : "*";
      params.append("fq", `price:[${a} TO ${b}]`);
    }

    // Ask BR to return a price-range facet so the price filter has
    // bucket counts (not yet rendered, but useful for v2).
    params.append("facet.range", "price");

    // NOTE: we intentionally do NOT send `facet.field`. Which facets come
    // back is owned by BR (Algo defaults + whatever merchandisers pin in the
    // Discovery dashboard); the drawer renders whatever BR returns. Hard-coding
    // facet.field in the request duplicated that responsibility and produced
    // long, redundant param lists — removed per BR guidance.

    const host =
      cfg.host ||
      (window.shop && window.shop.br && window.shop.br.host) ||
      DEFAULT_ENDPOINT;
    return host + "?" + params.toString();
  }

  // BR best-practice audit: keep rows <= 48 on every request and paginate.
  // No single call exceeds 48 — larger page sizes ("All", 48-tab) fill via
  // start-based pagination / the "All" background cascade instead.
  const BR_ROWS_CAP = 48;
  // Progressive "All": paint the first 48 fast, then fill the rest in the
  // background (48 at a time) so a big collection doesn't block first paint or
  // fire hundreds of swatch-hydrate requests at once.
  const FIRST_PAINT_ROWS = 48;
  const BG_CHUNK_ROWS = 48;
  // Back-nav / deep-page RESTORE only: build the grid in ONE big request
  // instead of the <=48 cascade, so a Back from a PDP lands at the saved
  // scroll position IMMEDIATELY and PRECISELY — the grid is already at full
  // height before we scroll, instead of "top, then scroll" as N pages stream
  // in. BR hard-caps `rows` (~240 → HTTP 400), so 200 is the safe ceiling;
  // deeper positions get one 200-row shot + a short tail cascade. Fresh
  // page-1 loads keep the fast <=48 first paint + background cascade. This is
  // a deliberate, scoped deviation from the "rows <= 48" audit for the
  // restore path only (team request: perceive an immediate scrolled load).
  const RESTORE_MAX_ROWS = 200;

  // Read state from the current URL using Shopify-style params. The
  // page size is read from ?per_page= (set by the toolbar tabs) and
  // becomes state.rows — so Load More appends exactly one page-size
  // worth of items each click, matching whichever tab is active.
  function hydrateFromUrl(defaultRows) {
    const u = new URL(window.location.href);
    const filters = {};

    // filter.v.option.<name> / filter.p.<key> for the facets with a
    // Shopify equivalent, plus filter.br.<BrFacetName> for everything
    // else (CATEGORY and any facet BR adds later). Without the second
    // branch those filters were dropped on reload / share / back.
    u.searchParams.forEach((value, key) => {
      let facet = FILTER_MAP[key];
      if (!facet && key.indexOf(BR_PARAM_PREFIX) === 0) {
        facet = key.slice(BR_PARAM_PREFIX.length);
      }
      if (!facet) return;
      if (!filters[facet]) filters[facet] = [];
      const nv = normFacetValue(facet, value);
      if (!filters[facet].includes(nv)) filters[facet].push(nv);
    });

    const priceMin = u.searchParams.get("filter.v.price.gte");
    const priceMax = u.searchParams.get("filter.v.price.lte");
    const sortBy = u.searchParams.get("sort_by") || "";
    const page = Math.max(1, parseInt(u.searchParams.get("page") || "1", 10));

    // Page size sources, in order: ?per_page=, then the section default.
    // `effective` folds both together so the section default of "All"
    // (>=1000) cascades on first load exactly like the ?per_page=1000
    // tab — otherwise a default of All would only fetch one BR_ROWS_CAP
    // page and stop.
    const perPageParam = u.searchParams.get("per_page");
    const effective = perPageParam ? parseInt(perPageParam, 10) : defaultRows;
    const isAll = effective >= 1000;
    // `pageSize` = how many items one "page" (per-page tab / Load More step)
    // shows. Fetches are ALWAYS chunked to BR_ROWS_CAP (<=48 per BR audit);
    // any page size above that (120, All) is filled by cascadeUntil() in
    // <=48-row chunks, so no single request exceeds the cap.
    const pageSize = isAll ? Infinity : effective > 0 ? effective : defaultRows;
    const rows = FIRST_PAINT_ROWS; // the <=48 first-paint chunk

    return {
      filters,
      priceMin: priceMin ? Number(priceMin) : null,
      priceMax: priceMax ? Number(priceMax) : null,
      sort: SORT_MAP[sortBy] != null ? SORT_MAP[sortBy] : "",
      sortBy, // raw Shopify value, for <select>
      page,
      rows,
      pageSize, // items per "page" (Infinity = All)
      perPageRaw: perPageParam || "", // raw value for URL round-trip
      // Fresh loads always start at 0 and cascade-fill up to page*pageSize
      // (cumulative Load-More grid), so a ?page=N refresh restores the same view.
      start: 0,
      // Pathname + query string. BR's "URL contains" audience rules (used to
      // target ad-driven ranking rules by utm_content/utm_campaign/etc.) match
      // against the `url` request param built from this value (see
      // buildRequestUrl) — a bare pathname strips every query param before BR
      // ever sees it, so no UTM-based audience could ever match live traffic
      // (BR confirmed their side works when `url` includes the query string;
      // the gap was here). syncUrl() never deletes unrecognized params (like
      // utm_content), so this keeps matching across filter/sort/pagination
      // interactions on the same landing, not just the first paint.
      path: u.pathname + u.search,
    };
  }

  // Push current state into the URL without reloading. Keeps shareable
  // URLs and the browser back button working.
  function syncUrl(state, opts) {
    const u = new URL(window.location.href);

    // Strip all filter / sort / page params we own; rebuild from state.
    const stripKeys = [
      "sort_by",
      "page",
      "per_page",
      "filter.v.price.gte",
      "filter.v.price.lte",
    ];
    Object.keys(FILTER_MAP).forEach((k) => stripKeys.push(k));
    // Any filter.br.* from a previous state — collect first, since
    // deleting while iterating searchParams is unsafe.
    const brKeys = [];
    u.searchParams.forEach((_v, k) => {
      if (k.indexOf(BR_PARAM_PREFIX) === 0) brKeys.push(k);
    });
    brKeys.forEach((k) => stripKeys.push(k));
    stripKeys.forEach((k) => u.searchParams.delete(k));

    Object.keys(state.filters).forEach((facetField) => {
      // Facets with no Shopify equivalent round-trip as filter.br.<name>
      // rather than being dropped from the URL entirely.
      const shopifyKey =
        prettyParamFor(facetField) || BR_PARAM_PREFIX + facetField;
      (state.filters[facetField] || []).forEach((v) =>
        u.searchParams.append(shopifyKey, v),
      );
    });
    if (state.priceMin != null)
      u.searchParams.set("filter.v.price.gte", state.priceMin);
    if (state.priceMax != null)
      u.searchParams.set("filter.v.price.lte", state.priceMax);
    if (state.sortBy) u.searchParams.set("sort_by", state.sortBy);
    if (state.page > 1) u.searchParams.set("page", state.page);
    if (state.perPageRaw) u.searchParams.set("per_page", state.perPageRaw);

    const fn = opts && opts.replace ? "replaceState" : "pushState";
    // Preserve bpPlpScroll on the history entry — Safari/iOS restore scroll
    // from history.state on Back, and wiping it here would break that.
    const prev =
      history.state && typeof history.state === "object" ? history.state : {};
    const scrollY =
      typeof prev.bpPlpScroll === "number" && prev.bpPlpScroll > 0
        ? prev.bpPlpScroll
        : Math.round(
            window.scrollY ||
              window.pageYOffset ||
              document.documentElement.scrollTop ||
              0,
          );
    history[fn](
      Object.assign({}, prev, { bpBr: state, bpPlpScroll: scrollY > 0 ? scrollY : prev.bpPlpScroll }),
      "",
      u.toString(),
    );
  }

  // Applied-filter chips above the grid — one per active facet value plus
  // an optional price chip, and a "Clear all". Mirrors state.filters so
  // the shopper sees + removes selections without opening the drawer.
  function renderActiveFilters(root, state) {
    const wrap = root.querySelector("[data-active-filters]");
    if (!wrap) return;
    const chips = [];
    Object.keys(state.filters || {}).forEach((facet) => {
      (state.filters[facet] || []).forEach((val) => {
        const text = `${labelFor(facet)}: ${displayFacetValue(facet, val)}`;
        chips.push(
          `<button type="button" class="bp-coll__chip" data-chip-facet="${escapeHtml(facet)}" data-chip-value="${escapeHtml(val)}" aria-label="Remove filter ${escapeHtml(text)}">` +
            `<span>${escapeHtml(text)}</span>` +
            `<span class="bp-coll__chip-x" aria-hidden="true">✕</span></button>`,
        );
      });
    });
    if (state.priceMin != null || state.priceMax != null) {
      const lo = state.priceMin != null ? money(state.priceMin) : "";
      const hi = state.priceMax != null ? money(state.priceMax) : "";
      const label = lo && hi ? `${lo} – ${hi}` : lo ? `${lo}+` : `Up to ${hi}`;
      chips.push(
        `<button type="button" class="bp-coll__chip" data-chip-price="1" aria-label="Remove price filter">` +
          `<span>Price: ${escapeHtml(label)}</span>` +
          `<span class="bp-coll__chip-x" aria-hidden="true">✕</span></button>`,
      );
    }
    if (!chips.length) {
      wrap.hidden = true;
      wrap.innerHTML = "";
      return;
    }
    chips.push(
      `<button type="button" class="bp-coll__chip bp-coll__chip--clear" data-chip-clear="1">Clear all</button>`,
    );
    wrap.innerHTML = chips.join("");
    wrap.hidden = false;
  }

  // Render BR facets into the existing drawer body. Pills carry
  // Shopify-style data-param so the rest of the drawer UI (counts,
  // staged-active, Apply/Clear) keeps working unchanged.
  function renderFacets(drawer, facets, activeFilters) {
    const body = drawer.querySelector(".bp-filter-drawer__body");
    if (!body) return;

    // The colour-swatch metaobject map (bp-shop-globals.liquid) loads async on
    // the first PLP view of a session, so the facet drawer can render before it
    // arrives. Re-paint the already-rendered swatches once the map lands.
    // Bound once; cached views are synchronous and never fire this.
    if (!renderFacets.__swatchBound && typeof window !== "undefined") {
      renderFacets.__swatchBound = true;
      window.addEventListener("bp:color-swatches-ready", () => {
        document
          .querySelectorAll(".bp-filter__swatch[data-bp-swatch-name]")
          .forEach((el) => {
            const bg = swatchBackground(el.getAttribute("data-bp-swatch-name"));
            if (bg) el.style.background = bg;
          });
      });
    }

    // Keep the price filter if one exists (Liquid renders it from
    // Shopify's collection.filters); otherwise emit a minimal one so
    // BR PLPs whose collection has NO native price_range filter still get a
    // price input (QA 7/24: the drawer showed Color/Size/Brand/Category but
    // no Price on such collections). BR always returns a PRICE number_range
    // facet, so the input still filters. Structure mirrors the Liquid form
    // (bp-collection.liquid) so the existing price handlers keep working.
    const priceDetails = body.querySelector(
      "[data-bp-filter] form[data-bp-price-form]",
    );
    let priceHtml = priceDetails
      ? priceDetails.closest("[data-bp-filter]").outerHTML
      : "";
    if (!priceHtml) {
      let maxPlaceholder = "";
      const priceFacet = (facets || []).find((f) => f && f.type === "number_range");
      if (priceFacet && Array.isArray(priceFacet.value) && priceFacet.value.length) {
        const ends = priceFacet.value
          .map((v) => Number(v && (v.end != null ? v.end : v.high)))
          .filter((n) => !isNaN(n));
        if (ends.length) maxPlaceholder = String(Math.ceil(Math.max.apply(null, ends)));
      }
      priceHtml =
        '<details class="bp-filter" data-bp-filter>' +
        '<summary class="bp-filter__summary">' +
        '<span class="bp-filter__label">Price</span>' +
        '<span class="bp-filter__chevron" aria-hidden="true">' +
        '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        "</span></summary>" +
        '<div class="bp-filter__body">' +
        '<form class="bp-filter__price" data-bp-price-form method="get">' +
        '<label class="bp-filter__price-field"><span class="bp-filter__price-label">Min</span>' +
        '<input type="number" name="filter.v.price.gte" min="0" placeholder="0" class="bp-filter__price-input"></label>' +
        '<label class="bp-filter__price-field"><span class="bp-filter__price-label">Max</span>' +
        '<input type="number" name="filter.v.price.lte" min="0" placeholder="' +
        escapeHtml(maxPlaceholder) +
        '" class="bp-filter__price-input"></label>' +
        '<button type="submit" class="bp-filter__price-apply">Apply</button>' +
        "</form></div></details>";
    }

    const parts = [];

    // Order facets: Color → Size → Brand → everything else in BR's own
    // order. Ranked on the NORMALIZED name — the old version matched the
    // literal strings 'colors'/'sizes'/'brand', so when BR renamed them
    // to COLOR/SIZE/BRAND nothing matched and the intended ordering was
    // quietly lost. sort() is stable, so same-rank facets keep BR order.
    const ordered = facets
      .slice()
      .sort((a, b) => facetRank(a.name) - facetRank(b.name));

    ordered.forEach((facet) => {
      if (!facet || !facet.value || !facet.value.length) return;

      // Skip range facets. BR's PRICE facet is type "number_range" and
      // its buckets are {start, end, count} with no `name`, so the pill
      // branch below rendered them as unlabeled count-only chips under a
      // second "Price" accordion. The min/max form (priceHtml) is the
      // price UI. Detect by TYPE, not by name: this was previously
      // `facetName === 'price'` and silently stopped matching the moment
      // BR renamed the facet to "PRICE".
      if (facet.type === "number_range") return;

      // Anything else without usable labels is unrenderable — pills would
      // read "(3)" with no text and filter nothing. This also covers the
      // legacy unnamed `category` facet (200 valueless entries) without
      // hiding the real, named CATEGORY facet.
      const values = facet.value.filter(
        (v) => v && v.name != null && String(v.name).trim() !== "",
      );
      if (!values.length) return;

      // `brField` is what goes into `fq` — the facet name EXACTLY as BR
      // returned it. `shopifyKey` is only for the URL.
      const brField = facet.name;
      const shopifyKey = prettyParamFor(brField) || BR_PARAM_PREFIX + brField;
      const label = labelFor(brField);
      const n = normFacet(brField);
      const isColor = n === "color" || n === "colors";
      const active = activeValuesFor(activeFilters, brField);
      const activeCount = active.length;

      // a11y: stay a plain <a>, no role="button"/aria-pressed — Space
      // wouldn't activate it (no keydown handler), a role/behavior
      // mismatch. Selected state uses aria-current, matching the
      // server-rendered pill fallback in bp-collection.liquid.
      //
      // DEV-1591: a zero-count, non-active value renders as a real
      // disabled <button> instead — no href, not focusable, not
      // clickable — matching the Liquid-rendered fallback in
      // bp-collection.liquid / bp-search-results.liquid. An <a
      // aria-disabled> keeps the href reachable via a screen reader's
      // links list or a middle-click.
      const pills = values
        .map((v) => {
          const isActive = active.includes(normFacetValue(brField, v.name));
          const isDisabled = !isActive && Number(v.count) === 0;
          const swatchHtml = isColor
            ? `<span class="bp-filter__swatch" aria-hidden="true" data-bp-swatch-name="${escapeHtml(String(v.name))}" style="background:${escapeHtml(swatchBackground(v.name))};"></span>`
            : "";
          const classAttr = `bp-filter__pill${isActive ? " is-active" : ""}${isDisabled ? " is-disabled" : ""}${isColor ? " bp-filter__pill--swatch" : ""}`;
          const labelHtml = `${swatchHtml}<span class="bp-filter__pill-label">${escapeHtml(v.name)} (${v.count})</span>`;
          if (isDisabled) {
            return `
            <button type="button"
               class="${classAttr}"
               data-bp-pill
               data-bp-facet="${escapeHtml(brField)}"
               data-param="${escapeHtml(shopifyKey)}"
               data-value="${escapeHtml(v.name)}"
               disabled>
              ${labelHtml}
            </button>`;
          }
          return `
          <a href="#"
             class="${classAttr}"
             data-bp-pill
             data-bp-facet="${escapeHtml(brField)}"
             data-param="${escapeHtml(shopifyKey)}"
             data-value="${escapeHtml(v.name)}"
             ${isActive ? 'aria-current="true"' : ""}>
            ${labelHtml}
          </a>`;
        })
        .join("");

      parts.push(`
        <details class="bp-filter${activeCount > 0 ? "" : ""}" ${activeCount > 0 ? "open" : ""} data-bp-filter>
          <summary class="bp-filter__summary">
            <span class="bp-filter__label">${escapeHtml(label)}</span>
            ${activeCount > 0 ? `<span class="bp-filter__count">${activeCount} Selected</span>` : ""}
            <span class="bp-filter__chevron" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </summary>
          <div class="bp-filter__body">
            <div class="bp-filter__pills${isColor ? " bp-filter__pills--swatches" : ""}">
              ${pills}
            </div>
          </div>
        </details>
      `);
    });

    body.innerHTML = priceHtml + parts.join("");
  }

  // Ensure every ACTIVE filter is represented in the facet list, even when
  // BR's response dropped it. A 0-/few-result response returns only the PRICE
  // range facet (BR computes no value facets when nothing matches), so the
  // shopper's own selection vanished from the drawer — they couldn't see or
  // clear the filters that produced the empty result (QA 7/24, Dragana). For
  // any active filter not present in `facets`, synthesize a text facet whose
  // only values are the selected ones (count 0 — BR gave no counts). Those
  // render as active, removable pills so the shopper can always broaden back.
  function augmentWithActive(facets, filters) {
    // Shallow-copy each facet so we can extend its value list without mutating
    // the caller's objects (e.g. a cached facet set). concat() below returns a
    // new array, so the original value arrays are untouched.
    const list = (facets || []).map((f) => Object.assign({}, f));
    const byNorm = {};
    list.forEach((f) => { if (f && f.name != null) byNorm[normFacet(f.name)] = f; });

    Object.keys(filters || {}).forEach((field) => {
      const vals = filters[field] || [];
      if (!vals.length) return;
      const nf = normFacet(field);
      const existing = byNorm[nf];
      if (existing && existing.type !== "number_range") {
        // Facet present but BR may have dropped the selected value(s) (0 count
        // after this selection). Add any missing active value so the shopper's
        // choice stays visible + removable, not just the still-available ones.
        const present = {};
        (existing.value || []).forEach((v) => {
          if (v && v.name != null) present[String(v.name)] = true;
        });
        const add = vals
          .filter((v) => !present[String(v)])
          .map((v) => ({ name: v, count: 0 }));
        if (add.length) existing.value = (existing.value || []).concat(add);
      } else if (!existing) {
        // Facet entirely absent (0-result response) — synthesize it.
        const f = {
          name: field,
          type: "text",
          value: vals.map((v) => ({ name: v, count: 0 })),
        };
        list.push(f);
        byNorm[nf] = f;
      }
    });
    return list;
  }

  // ---- Bloomreach content-injection tiles -----------------------
  // Promo + Shop-the-Look tiles now come from Bloomreach's content
  // injection (`content_injection=true`), NOT from Liquid section
  // blocks. Content docs are interleaved into `response.docs` at their
  // merchandised slots and carry:
  //   docType     "content"
  //   contentType "image_url" / "banner_url" (image holds an <img> blob)
  //   content     the raw asset (an <img …> string for image_url)
  //   dimension   "1x1" | "1x2" | "2x2"  → tile size / grid span
  //   slots       absolute grid positions the asset occupies
  //   metaInfo    JSON: title, description, ctaText, ctaUrl,
  //               backgroundColor, textColor, CTA colours
  //               (ctaBackgroundColor / ctaTextColor +
  //               ctaBackgroundColorHover / ctaTextColorHover), plus
  //               (STL) action / stlStyle / collectionHandle
  // BR repeats a content doc once per occupied slot (a 2x2 appears 4×),
  // so we dedup by identity and render each tile once — the .bp-tile
  // span classes handle the visual size + responsive collapse.

  function isContentDoc(doc) {
    return doc && String(doc.docType || "").toLowerCase() === "content";
  }

  // Stable identity for a content doc so per-slot repeats collapse to one
  // rendered tile.
  function contentKey(doc) {
    const slots = Array.isArray(doc.slots) ? doc.slots.join(",") : "";
    return slots + "|" + (doc.content || "") + "|" + (doc.metaInfo || "");
  }

  // Parse metaInfo: prefer JSON; tolerate the older "key: value" line
  // format BR emitted before the schema settled.
  function parseMetaInfo(raw) {
    if (!raw) return {};
    if (typeof raw === "object") return raw;
    const s = String(raw).trim();
    if (!s) return {};
    try {
      const j = JSON.parse(s);
      if (j && typeof j === "object") return j;
    } catch (_) {
      /* fall through to key:value */
    }
    const out = {};
    s.split(/\r?\n/).forEach((line) => {
      const i = line.indexOf(":");
      if (i === -1) return;
      const k = line.slice(0, i).trim();
      const v = line.slice(i + 1).trim();
      if (k) out[k] = v;
    });
    return out;
  }

  // Pull a usable image src out of BR's `content` HTML blob. Only http(s)
  // URLs pass — anything else → no image (tile falls back to bg color).
  function contentImageSrc(content) {
    const m = String(content || "").match(/<img[^>]+src=["']([^"']+)["']/i);
    const src = m ? m[1] : "";
    return /^https?:\/\//i.test(src) ? src : "";
  }

  // Safe click target — same-origin paths or http(s) only (rejects
  // javascript:/data: URLs merchandised into metaInfo).
  function safeUrl(u) {
    const s = String(u == null ? "" : u).trim();
    if (!s) return "";
    if (s.charAt(0) === "/") return s;
    if (/^https?:\/\//i.test(s)) return s;
    return "";
  }

  // Sanitize a merchandised color to a hex or CSS keyword before it lands
  // in a style="" attribute.
  function safeColor(c) {
    const s = String(c == null ? "" : c).trim();
    if (/^#[0-9a-fA-F]{3,8}$/.test(s)) return s;
    if (/^[a-zA-Z]+$/.test(s)) return s.toLowerCase();
    return "";
  }

  // Merchandised image overlay (a darkening scrim over the tile photo).
  // metaInfo `overlay` is a percent 0–100 ("40" → 0.40 opacity); missing,
  // empty, 0, or non-numeric → 0 (no scrim, image shows at 100%). Optional
  // `overlayColor` tints it (hex/keyword, default black).
  function overlayOpacity(raw) {
    if (raw == null || raw === "") return 0;
    const n = parseFloat(String(raw).replace(/[^0-9.]/g, ""));
    if (!isFinite(n) || n <= 0) return 0;
    return Math.min(n, 100) / 100;
  }

  const DIMENSION_MAP = {
    "1x1": { size: "1x1", span: "bp-tile--span-1" },
    "1x2": { size: "1x2", span: "bp-tile--span-2" },
    "2x2": { size: "2x2", span: "bp-tile--span-4" },
  };

  // metaInfo.contentPosition → flex placement of the copy block inside the
  // tile. Nine keywords "{vertical}-{horizontal}", default "center-center".
  // Both "center" and "middle" mean centered; the copy wrapper is a flex
  // column, so justify-content is the vertical axis and align-items the
  // horizontal. text-align follows the anchor so copy reads left/center/
  // right consistently.
  function placementStyle(pos) {
    const parts = String(pos || "center-center")
      .toLowerCase()
      .trim()
      .split("-");
    const vert = parts[0] || "center";
    const horiz = parts[1] || "center";
    const vMap = {
      top: "flex-start",
      center: "center",
      middle: "center",
      bottom: "flex-end",
    };
    const hMap = {
      left: "flex-start",
      center: "center",
      middle: "center",
      right: "flex-end",
    };
    const tMap = {
      left: "left",
      center: "center",
      middle: "center",
      right: "right",
    };
    return (
      "justify-content:" +
      (vMap[vert] || "center") +
      ";align-items:" +
      (hMap[horiz] || "center") +
      ";text-align:" +
      (tMap[horiz] || "center")
    );
  }

  // Normalize CTAs to up to two {text,url,variant} entries. Preferred shape
  // is metaInfo.ctas: [{text,url,variant}]; the legacy single ctaText/ctaUrl
  // pair still works (rendered as one primary CTA).
  function normalizeCtas(meta) {
    let list = [];
    if (Array.isArray(meta.ctas)) {
      list = meta.ctas
        .map((c) => ({
          text: String((c && (c.text || c.label)) || "").trim(),
          url: safeUrl(c && c.url),
          variant:
            String((c && c.variant) || "primary").toLowerCase() === "secondary"
              ? "secondary"
              : "primary",
          // Per-CTA colours — BR Media Rules nests these inside each ctas[]
          // item so multiple CTAs can differ. safeColor'd ('' when unset); the
          // top-level meta.cta*Color acts as the fallback at render time.
          bg: safeColor(c && c.ctaBackgroundColor),
          fg: safeColor(c && c.ctaTextColor),
          bgH: safeColor(c && c.ctaBackgroundColorHover),
          fgH: safeColor(c && c.ctaTextColorHover),
        }))
        .filter((c) => c.text);
    } else {
      const t = String(meta.ctaText || "").trim();
      if (t)
        list = [
          {
            text: t,
            url: safeUrl(meta.ctaUrl || meta.url),
            variant: "primary",
          },
        ];
    }
    return list.slice(0, 2);
  }

  // Standardized "Shop the look" CTA for shop-the-look content tiles (Brooke).
  // When a BR promo spot's metaInfo marks it shop-the-look (action ==
  // "shop_the_look"), the theme FORCES this CTA — pinned top-left, fixed copy
  // + bag icon — overriding whatever CTA copy/style/colours the spot otherwise
  // merchandises, so every shop-the-look spot reads identically and merchants
  // don't have to set it per spot. The bag glyph is inlined (NOT the white
  // assets/bp-bag-icon.svg, which is the dark-button variant) with
  // fill="currentColor" so it inherits the CTA text colour
  // (var(--bp-color-text)); sized 16×16.
  const STL_CTA_LABEL = "Shop the look";
  const STL_BAG_ICON =
    '<svg class="bp-tile__stl-cta-icon" width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M4.18929 5.74443H16.5343H16.6292L16.6729 5.74559C16.9007 5.75108 17.1328 5.87813 17.2448 6.08099C17.3145 6.20166 17.3382 6.30382 17.3547 6.43057C17.6354 9.18815 17.9008 11.9961 18.3051 14.7357C18.3771 15.2468 18.4466 15.7581 18.5137 16.2698C18.5163 16.2918 18.5195 16.336 18.5207 16.3583C18.5378 16.6194 18.4156 16.9264 18.1508 17.0688C17.9651 17.1753 17.7807 17.1764 17.5845 17.1756C16.1978 17.1756 4.69428 17.1756 3.34023 17.1756C3.20422 17.1727 3.01496 17.1863 2.86505 17.1452C2.45036 17.0407 2.25213 16.6561 2.31666 16.2446C2.41071 15.5272 2.58579 14.249 2.68621 13.5186C2.95476 11.54 3.15849 9.46683 3.35962 7.47468C3.37582 7.35719 3.44296 6.63748 3.45714 6.52896L3.46698 6.43433L3.46814 6.42246C3.46814 6.41957 3.46901 6.41349 3.47045 6.40568C3.5156 6.02427 3.79254 5.74038 4.18929 5.74443ZM4.18929 6.61259C4.2136 6.61259 4.24804 6.5961 4.27872 6.57353C4.30302 6.555 4.3201 6.53099 4.32704 6.51854C4.33457 6.5061 4.33486 6.50147 4.33399 6.50436C4.33341 6.50812 4.33225 6.51362 4.33138 6.51767C4.33138 6.51767 4.33081 6.51941 4.33052 6.52433L4.32936 6.53619L4.31923 6.63053C4.132 8.41519 3.9054 10.3182 3.74624 12.1017C3.63685 13.4482 3.56103 15.0257 3.46727 16.3754L3.46582 16.3916V16.3962C3.46582 16.3962 3.46611 16.3974 3.4664 16.3916C3.4664 16.3873 3.4664 16.3751 3.46351 16.3589C3.45396 16.2909 3.41721 16.2162 3.3602 16.1508C3.29306 16.0799 3.20422 16.0247 3.12319 16.0163C3.11856 16.0163 3.13361 16.0174 3.15647 16.0177C4.67199 16.0183 16.0117 16.0177 17.5848 16.0177H17.6719C17.6823 16.0171 17.6916 16.0168 17.6962 16.0166C17.7014 16.0163 17.7034 16.016 17.6991 16.0163C17.5752 16.0287 17.4268 16.1592 17.3857 16.2796C17.3576 16.3557 17.3617 16.3789 17.362 16.3942C17.362 16.3922 17.362 16.3931 17.362 16.3887L17.3605 16.3725C17.254 14.8544 17.1872 13.3293 17.0509 11.8149C16.8906 10.0896 16.6752 8.23172 16.4883 6.50465C16.495 6.52028 16.5161 6.55443 16.5468 6.57584C16.5714 6.59494 16.5989 6.60594 16.6145 6.6097L16.6243 6.61201H16.6284C16.6284 6.61201 16.6298 6.61288 16.6304 6.61288C16.6318 6.61288 16.6336 6.61288 16.633 6.61288C16.6324 6.61288 16.635 6.61288 16.6292 6.61288C16.5783 6.6123 16.4 6.61346 16.3445 6.61288H4.18929V6.61259Z" fill="currentColor"/>' +
    '<path d="M6.08276 8.7807C6.17855 7.51897 6.20633 6.21411 6.58514 4.98913C7.15408 3.11013 8.44186 2.11637 10.4138 2.12679C13.7999 2.13345 14.5028 4.88003 14.6718 7.71401C14.6961 8.07459 14.7155 8.43459 14.7447 8.78041L13.5872 8.77491C13.617 8.46064 13.6572 8.02655 13.6752 7.71864C13.8008 5.83357 13.5973 3.34135 11.3985 2.81611C10.7573 2.66766 10.07 2.66766 9.42867 2.81611C7.61479 3.24817 7.15119 5.03746 7.12659 6.68726C7.11096 7.36935 7.17521 8.09138 7.24032 8.77491L6.08276 8.78041V8.7807Z" fill="currentColor"/>' +
    "</svg>";

  // Render one BR content tile as a .bp-tile <li> (reusing the section's
  // existing tile CSS). Two click modes:
  //   link           → whole tile <a> (to metaInfo url) + up to 2 CTA links
  //   shop_the_look  → whole tile <button> that opens the reusable STL
  //                    drawer (stlStyle / collectionHandle). The per-spot CTAs
  //                    are ignored; a standardized "Shop the look" CTA (bag
  //                    icon, pinned top-left) is forced instead.
  // Copy placement comes from metaInfo.contentPosition (9-position).
  // Returns '' for an empty demo insert (no image, no copy).
  function renderContentTile(doc) {
    const meta = parseMetaInfo(doc.metaInfo);
    const dim =
      DIMENSION_MAP[String(doc.dimension || "1x1")] || DIMENSION_MAP["1x1"];
    const img = contentImageSrc(doc.content);
    const title = String(meta.title || "").trim();
    const desc = String(meta.description || "").trim();
    const ctas = normalizeCtas(meta);
    const action = String(meta.action || "link")
      .trim()
      .toLowerCase();
    const isStl =
      action === "shop_the_look" ||
      action === "shop-the-look" ||
      action === "stl";

    // Nothing to show → skip (unconfigured demo slot).
    if (!img && !title && !desc && !ctas.length) return "";

    const variant = img ? "overlay" : "content";
    const bg = safeColor(meta.backgroundColor);
    const fg = safeColor(meta.textColor);
    const styleParts = [];
    if (bg) styleParts.push("--bp-tile-bg:" + bg);
    if (fg) styleParts.push("--bp-tile-text:" + fg);
    // Per-tile CTA colours + hover colours from the BR banner metaInfo
    // (Brooke: merchants couldn't set CTA hover colours, so hovering hid the
    // copy). ctaBackgroundColor / ctaTextColor / ctaBackgroundColorHover /
    // ctaTextColorHover → CSS vars the .bp-tile--cta-custom rules consume for
    // BOTH the base and hover states, so the label stays visible on hover.
    const ctaBg = safeColor(meta.ctaBackgroundColor);
    const ctaFg = safeColor(meta.ctaTextColor);
    const ctaBgH = safeColor(meta.ctaBackgroundColorHover);
    const ctaFgH = safeColor(meta.ctaTextColorHover);
    if (ctaBg) styleParts.push("--bp-tile-cta-bg:" + ctaBg);
    if (ctaFg) styleParts.push("--bp-tile-cta-text:" + ctaFg);
    if (ctaBgH) styleParts.push("--bp-tile-cta-bg-hover:" + ctaBgH);
    if (ctaFgH) styleParts.push("--bp-tile-cta-text-hover:" + ctaFgH);
    // STL tiles get the standardized CTA below, so the merchandised CTA
    // colours never apply — force ctaCustom off for them. Enable custom-CTA
    // styling when EITHER the top-level colours or any per-CTA colour (nested
    // in a ctas[] item) is set, so nested-per-CTA metadata works.
    const anyCtaColor = ctas.some((c) => c.bg || c.fg || c.bgH || c.fgH);
    const ctaCustom = !isStl && !!(ctaBg || ctaFg || ctaBgH || ctaFgH || anyCtaColor);
    const style = styleParts.length
      ? ' style="' + styleParts.join(";") + '"'
      : "";

    const bgMedia = img
      ? `<img class="bp-tile__bg-media" src="${escapeHtml(img)}" alt="${escapeHtml(title)}" loading="lazy">`
      : "";

    // Optional darkening overlay over the image (metaInfo `overlay` percent).
    // Only when there's an image; absent/0 → no scrim, image at 100%.
    const scrimOpacity = img ? overlayOpacity(meta.overlay) : 0;
    const scrim =
      scrimOpacity > 0
        ? `<div class="bp-tile__scrim" style="background:${safeColor(meta.overlayColor) || "#000000"};opacity:${scrimOpacity}"></div>`
        : "";

    const primaryUrl = (
      ctas.find((c) => c.variant === "primary") ||
      ctas[0] ||
      {}
    ).url;

    let cover = "";
    if (isStl) {
      const stlStyle = String(meta.stlStyle || meta.bpStyle || "").trim();
      const stlColl = String(
        meta.collectionHandle || meta.collection || "",
      ).trim();
      // Only a trigger the drawer can act on (a style or a collection) is
      // worth wiring; otherwise fall back to a plain (dead) cover.
      if (stlStyle || stlColl) {
        cover =
          `<button type="button" class="bp-tile__cover bp-tile__cover--button" ` +
          `data-stl-trigger data-drawer-id="bp-br-content" ` +
          (stlStyle ? `data-bp-stl-style="${escapeHtml(stlStyle)}" ` : "") +
          (stlColl ? `data-bp-stl-collection="${escapeHtml(stlColl)}" ` : "") +
          (title ? `data-bp-stl-title="${escapeHtml(title)}" ` : "") +
          `aria-label="${escapeHtml(title || "Shop the Look")}"></button>`;
      }
    } else {
      // Whole-tile link: an explicit metaInfo url wins; otherwise fall back
      // to the primary CTA's destination so clicking anywhere still works.
      const coverUrl = safeUrl(meta.url || meta.ctaUrl) || primaryUrl;
      if (coverUrl) {
        cover = `<a class="bp-tile__cover" href="${escapeHtml(coverUrl)}" aria-label="${escapeHtml(title || (ctas[0] && ctas[0].text) || "View")}"></a>`;
      }
    }

    // Link mode: each CTA is its own link. STL mode does NOT render the
    // per-spot CTAs — the whole tile is the trigger and a standardized
    // "Shop the look" CTA (stlCta, below) is forced instead.
    let cta = "";
    if (ctas.length && !isStl) {
      const items = ctas
        .map((c) => {
          const cls = `bp-tile__cta bp-tile__cta--${c.variant}`;
          // Per-CTA colour vars override the tile-level ones (set from the
          // top-level meta.cta*Color), so each button carries its own colours.
          const cp = [];
          if (c.bg) cp.push("--bp-tile-cta-bg:" + c.bg);
          if (c.fg) cp.push("--bp-tile-cta-text:" + c.fg);
          if (c.bgH) cp.push("--bp-tile-cta-bg-hover:" + c.bgH);
          if (c.fgH) cp.push("--bp-tile-cta-text-hover:" + c.fgH);
          const cStyle = cp.length ? ` style="${cp.join(";")}"` : "";
          return `<a class="${cls}"${cStyle} href="${escapeHtml(c.url || "#")}">${escapeHtml(c.text)}</a>`;
        })
        .join("");
      cta = `<div class="bp-tile__ctas">${items}</div>`;
    }

    // Standardized STL CTA — "Shop the look" label + bag icon (icon on the
    // RIGHT of the label), pinned top-left of the tile. pointer-events:none
    // (via CSS) so clicks fall through to the whole-tile cover button.
    // Decorative (aria-hidden): the cover carries the accessible label.
    const stlCta = isStl
      ? `<div class="bp-tile__stl-cta" aria-hidden="true">` +
        `<span class="bp-tile__stl-cta-btn">` +
        `<span class="bp-tile__stl-cta-label">${escapeHtml(STL_CTA_LABEL)}</span>` +
        `${STL_BAG_ICON}` +
        `</span></div>`
      : "";

    const innerClass =
      variant === "overlay" ? "bp-tile__overlay" : "bp-tile__inner";
    const place = placementStyle(meta.contentPosition);
    const inner =
      `<div class="${innerClass}" style="${place}">` +
      (title ? `<h3 class="bp-tile__title">${escapeHtml(title)}</h3>` : "") +
      (desc ? `<p class="bp-tile__body">${escapeHtml(desc)}</p>` : "") +
      cta +
      `</div>`;

    const mode = isStl ? "shop_the_look" : "link";
    const ctaCustomClass = ctaCustom ? " bp-tile--cta-custom" : "";
    return `
      <li class="bp-coll__cell bp-tile bp-tile--${dim.size} bp-tile--variant-${variant} bp-tile--mode-${mode} ${dim.span}${ctaCustomClass}"
          data-bp-source="bloomreach-content"${style}>
        ${bgMedia}
        ${scrim}
        ${cover}
        ${stlCta}
        ${inner}
      </li>`;
  }

  function renderGrid(grid, docs, append) {
    // Content-doc identity is deduped across the WHOLE grid session (first
    // paint + every cascade / Load-More chunk), NOT per-fetch. BR interleaves
    // promo / Shop-the-Look tiles at their ABSOLUTE grid slots and repeats a
    // content doc once per occupied slot — so a spot merchandised below the
    // first page (e.g. slot 40) arrives in a later start>=48 append window, and
    // a multi-slot (2x2) tile can straddle a 48-row chunk boundary. The old
    // append branch filtered ALL content docs out ("appended pages are products
    // only"), so any promo below item 48 never rendered (Brooke). Rendering
    // them here instead needs cross-fetch dedup: a Set persisted on the grid so
    // a straddling tile isn't drawn twice. Reset on each fresh (non-append) load.
    if (!append) grid.__bpContentSeen = new Set();
    const seen = grid.__bpContentSeen || (grid.__bpContentSeen = new Set());

    // Walk docs in slot order: products render as cards, content docs render
    // once (deduped) as tiles. BR already orders docs by slot, so source order
    // == visual order; the .bp-tile span classes size the tiles and
    // grid-auto-flow dense backfills around them.
    const out = [];
    // First-screen product cards load their image eagerly (LCP); count PRODUCT
    // cards only so interleaved content tiles don't shift which cards are above
    // the fold. Appended chunks are always below the fold, so all lazy.
    const EAGER_CARDS = append ? 0 : 6;
    let productIdx = 0;
    docs.forEach((doc) => {
      if (isContentDoc(doc)) {
        const key = contentKey(doc);
        if (seen.has(key)) return;
        seen.add(key);
        const tile = renderContentTile(doc);
        if (tile) out.push(tile);
      } else {
        out.push(renderCard(normalizeDoc(doc), productIdx < EAGER_CARDS));
        productIdx += 1;
      }
    });
    if (append) {
      grid.insertAdjacentHTML("beforeend", out.join(""));
    } else {
      grid.innerHTML = out.join("");
    }
    // Sibling-color hydration is owned by assets/bp-card-hydrate.js
    // so the same logic also fixes the Liquid-rendered cards' broken
    // all_products[] 20-cap behavior on non-BR PLPs. We just hand off
    // the freshly rendered cards. The hydrate file loads alongside
    // this one from bp-collection.liquid; short poll guards load
    // order since both scripts are deferred.
    handOffToHydrate(grid);
    // Best Seller / Trending badges — window.BpBadges loads globally
    // from theme.liquid (defer), so poll the same way in case this
    // fetch resolves before it's ready.
    handOffTo(grid, "BpBadges", (api, scope) => api.applyToCards(scope));
  }

  // 0-result empty state: show a message (+ a Clear-all when filters are the
  // cause) after the grid, or remove it when results return. The toolbar +
  // filter drawer stay intact (see the facets guard in fetchPage), so the
  // shopper can still adjust the filters that produced the empty result.
  function setEmptyState(root, grid, numFound, state) {
    const existing = root.querySelector("[data-bp-br-empty]");
    if (numFound > 0) {
      if (existing) existing.remove();
      return;
    }
    const hasFilters =
      Object.keys(state.filters || {}).length > 0 ||
      state.priceMin != null ||
      state.priceMax != null;
    const msg = hasFilters
      ? "No products match the selected filters."
      : "No products found.";
    const clearBtn = hasFilters
      ? '<button type="button" class="bp-coll__loadmore" data-bp-br-empty-clear>Clear all filters</button>'
      : "";
    if (existing) {
      const t = existing.querySelector("[data-bp-br-empty-msg]");
      if (t) t.textContent = msg;
      const btn = existing.querySelector("[data-bp-br-empty-clear]");
      if (hasFilters && !btn)
        existing.insertAdjacentHTML("beforeend", clearBtn);
      if (!hasFilters && btn) btn.remove();
      return;
    }
    const el = document.createElement("div");
    el.className = "bp-coll__empty bp-coll__empty--br";
    el.setAttribute("data-bp-br-empty", "");
    el.innerHTML = `<p class="bp-coll__empty-text" data-bp-br-empty-msg>${msg}</p>${clearBtn}`;
    grid.insertAdjacentElement("afterend", el);
  }

  function handOffToHydrate(grid) {
    handOffTo(grid, "BpCardHydrate", (api, scope) => api.observeCards(scope));
  }

  function handOffTo(scope, globalName, run) {
    const tick = (left) => {
      const api = window[globalName];
      if (api) {
        run(api, scope);
        return;
      }
      if (left <= 0) return;
      setTimeout(() => tick(left - 100), 100);
    };
    tick(3000);
  }

  // Show a brief overlay during fetch. Reuses .is-loading from
  // bp-collection's Load More styles.
  function setLoading(root, on) {
    root.classList.toggle("bp-coll--loading", !!on);
  }

  function init(root, cfg) {
    const grid = root.querySelector("[data-grid]");
    const countEl = root.querySelector(".bp-coll__count");
    const sortSelect = root.querySelector("[data-sort-select]");
    const drawer = root.querySelector("dialog.bp-filter-drawer");
    const loadMoreContainer = root.querySelector(".bp-coll__pagination");

    if (!grid) {
      console.warn("[bp-bloomreach] No grid found; skipping BR takeover.");
      return;
    }

    const perPage = parseInt(cfg.perPage || "48", 10) || 48;
    BR_COLLECTION_HANDLE = cfg.collectionHandle || "";
    let state = hydrateFromUrl(perPage);
    state.catId = cfg.catId;

    // ---- Back-nav scroll restoration (PLP → PDP → Back) ----
    // Early snippet (bp-plp-restore-veil) owns SAVE: it tracks lastY and
    // intercepts product-link clicks so Safari cannot zero scrollY before
    // the value is written. This block owns RESTORE once the BR grid is tall
    // enough to hold the saved offset.
    try { if ("scrollRestoration" in history) history.scrollRestoration = "manual"; } catch (_) {}

    let pendingScrollY = null;
    let scrollCommitted = false;
    let scrollPinUntil = 0;
    let scrollPinRAF = 0;
    let userInterrupted = false;

    try {
      if (window.__bpPlpScroll && typeof window.__bpPlpScroll.readPending === "function") {
        pendingScrollY = window.__bpPlpScroll.readPending() || null;
      } else if (window.__bpPlpPendingScrollY > 0) {
        pendingScrollY = Math.round(window.__bpPlpPendingScrollY);
      }
    } catch (_) {}

    let scrollRevealed = pendingScrollY == null;
    let restoreVeil = null;

    function revealAfterRestore() {
      if (scrollRevealed) return;
      scrollRevealed = true;
      window.__bpPlpRestoring = false;
      window.__bpPlpPendingScrollY = 0;
      try {
        document.documentElement.classList.remove("bp-plp-restoring");
      } catch (_) {}
      try {
        if (window.__bpPlpScroll && typeof window.__bpPlpScroll.clearPending === "function") {
          window.__bpPlpScroll.clearPending();
        }
      } catch (_) {}
      try {
        const el = restoreVeil;
        restoreVeil = null;
        if (el) {
          el.classList.add("is-out");
          setTimeout(function () {
            if (el && el.parentNode) el.parentNode.removeChild(el);
          }, 450);
        }
      } catch (_) {}
    }

    if (pendingScrollY != null) {
      window.__bpPlpRestoring = true;
      try {
        document.documentElement.classList.add("bp-plp-restoring");
      } catch (_) {}
      try {
        restoreVeil = window.__bpRestoreVeil || null;
        window.__bpRestoreVeil = null;
        if (!restoreVeil) {
          restoreVeil = document.createElement("div");
          restoreVeil.className = "bp-plp-restore-veil";
          restoreVeil.setAttribute("aria-hidden", "true");
          restoreVeil.innerHTML = '<span class="bp-plp-restore-veil__spin"></span>';
          (document.body || document.documentElement).appendChild(restoreVeil);
        }
      } catch (_) {}
      setTimeout(revealAfterRestore, 8000);
    }

    function applyScrollInstant(y) {
      try {
        const html = document.documentElement;
        const body = document.body;
        const se = document.scrollingElement || html;
        html.classList.add("bp-plp-restoring");
        html.style.scrollBehavior = "auto";
        if (body) body.style.scrollBehavior = "auto";
        if (se) se.scrollTop = y;
        html.scrollTop = y;
        if (body) body.scrollTop = y;
        window.scrollTo(0, y);
      } catch (_) {}
    }

    function restoreFromSaved(y) {
      if (!(y > 0) || userInterrupted) return;
      window.__bpPlpRestoring = true;
      const apply = () => {
        if (userInterrupted) return;
        const maxY = Math.max(0, (document.scrollingElement || document.documentElement).scrollHeight - window.innerHeight);
        applyScrollInstant(Math.min(y, maxY));
      };
      apply();
      requestAnimationFrame(apply);
      [50, 100, 200, 400, 700, 1100, 1600].forEach((ms) => setTimeout(apply, ms));
    }

    // bfcache Back — grid DOM is already full height.
    window.addEventListener("pageshow", (e) => {
      if (!e.persisted) return;
      let y = 0;
      try {
        if (window.__bpPlpScroll) y = window.__bpPlpScroll.readPending() || 0;
      } catch (_) {}
      if (!(y > 0) && window.__bpPlpPendingScrollY > 0) y = Math.round(window.__bpPlpPendingScrollY);
      if (y > 0) {
        pendingScrollY = y;
        restoreFromSaved(y);
        setTimeout(() => {
          pendingScrollY = null;
          revealAfterRestore();
        }, 1700);
      }
    });

    // Deliberate user scroll cancels the pin. Ignore the first 700ms so the
    // Safari back-swipe / momentum does not abort restore.
    setTimeout(() => {
      const markInterrupted = () => {
        if (pendingScrollY == null) return;
        userInterrupted = true;
        pendingScrollY = null;
        revealAfterRestore();
      };
      window.addEventListener("wheel", markInterrupted, { passive: true, once: true });
      window.addEventListener("touchmove", markInterrupted, { passive: true, once: true });
    }, 700);

    function scrollPinTick() {
      scrollPinRAF = 0;
      if (pendingScrollY == null || userInterrupted) return;
      const se = document.scrollingElement || document.documentElement;
      const maxY = Math.max(0, se.scrollHeight - window.innerHeight);
      applyScrollInstant(Math.min(pendingScrollY, maxY));
      if (scrollCommitted && Date.now() >= scrollPinUntil) {
        pendingScrollY = null;
        requestAnimationFrame(revealAfterRestore);
        return;
      }
      scrollPinRAF = requestAnimationFrame(scrollPinTick);
    }

    function maybeRestoreScroll(done) {
      if (pendingScrollY == null || userInterrupted) return;
      if (done) {
        scrollCommitted = true;
        scrollPinUntil = Date.now() + 1000;
      }
      const se = document.scrollingElement || document.documentElement;
      const maxY = Math.max(0, se.scrollHeight - window.innerHeight);
      if (maxY >= pendingScrollY || done) {
        applyScrollInstant(Math.min(pendingScrollY, maxY));
        [40, 120, 260, 500, 900, 1400].forEach((ms) =>
          setTimeout(() => {
            if (pendingScrollY == null || userInterrupted) return;
            const m = Math.max(0, (document.scrollingElement || document.documentElement).scrollHeight - window.innerHeight);
            applyScrollInstant(Math.min(pendingScrollY, m));
          }, ms),
        );
        if (!scrollPinRAF) scrollPinRAF = requestAnimationFrame(scrollPinTick);
      }
      if (done) {
        setTimeout(() => {
          if (!userInterrupted && pendingScrollY != null) {
            const m = Math.max(0, (document.scrollingElement || document.documentElement).scrollHeight - window.innerHeight);
            applyScrollInstant(Math.min(pendingScrollY, m));
          }
          pendingScrollY = null;
          requestAnimationFrame(revealAfterRestore);
        }, 1100);
      }
    }
    state.searchQuery = cfg.searchQuery || "";

    // §9.4 card knobs — drive renderCard's bag trigger + data-attrs.
    if (cfg.cardQbStyle) CARD_CFG.qbStyle = cfg.cardQbStyle;
    if (cfg.cardQvDest) CARD_CFG.qvDest = cfg.cardQvDest;
    if (cfg.cardQvVisD) CARD_CFG.qvVisD = cfg.cardQvVisD;
    if (cfg.cardQvVisM) CARD_CFG.qvVisM = cfg.cardQvVisM;
    if (cfg.cardSecondaryHover != null)
      CARD_CFG.secondaryHover = !!cfg.cardSecondaryHover;

    // Price inputs no longer pre-fill in Liquid (they defaulted to the
    // collection's full range, which looked like an applied filter).
    // Reflect an ACTIVE price filter from the URL here instead, and use
    // the same helper to wipe the inputs on Clear.
    const setPriceInputs = (min, max) => {
      const form = drawer && drawer.querySelector("[data-bp-price-form]");
      if (!form) return;
      const minEl = form.querySelector('[name="filter.v.price.gte"]');
      const maxEl = form.querySelector('[name="filter.v.price.lte"]');
      if (minEl) minEl.value = min != null && min !== "" ? min : "";
      if (maxEl) maxEl.value = max != null && max !== "" ? max : "";
    };
    setPriceInputs(state.priceMin, state.priceMax);

    async function fetchPage(opts) {
      const append = !!(opts && opts.append);
      // Background = the automatic "All" cascade. It runs many chunk
      // fetches back-to-back; toggling the grid dim on each one made the
      // whole grid pulse (opacity 1 → 0.45 → 1) ~once per chunk. Skip the
      // loading state for background fetches so the grid stays stable while
      // the remainder streams in. Initial load + manual Load More still dim.
      const background = !!(opts && opts.background);
      if (!background) setLoading(root, true);
      const url = buildRequestUrl(cfg, state);
      try {
        const res = await fetch(url, { credentials: "omit" });
        if (!res.ok) throw new Error(`BR HTTP ${res.status}`);
        const json = await res.json();
        const docs = (json.response && json.response.docs) || [];
        const numFound = (json.response && json.response.numFound) || 0;
        const facets = (json.facet_counts && json.facet_counts.facets) || [];

        renderGrid(grid, docs, append);
        // If a Back-nav is waiting to restore scroll, try now — every render
        // (initial + each cascade chunk) grows the grid, so we restore the
        // moment it's tall enough to reach the saved offset.
        maybeRestoreScroll(false);
        // 0 results (usually an over-filtered set): show a clear empty-state
        // message + a Clear-all action instead of a bare "End of results"
        // button. Only the first page toggles the empty state — appended
        // pages never empty it.
        if (!append) setEmptyState(root, grid, numFound, state);
        // Wire the BR cards' bag buttons to the quick-add drawer. bp-collection
        // owns the handler (bindBagIcons → window.bpOpenProductDrawer); it's
        // idempotent per bag, so calling it after every render (initial /
        // append / re-fetch) is safe.
        if (typeof cfg.onRender === "function") cfg.onRender(grid);
        if (countEl) countEl.textContent = `${numFound} items`;
        // Re-render the drawer facets from BR's response, but keep the
        // shopper's ACTIVE filters visible even when the response dropped them.
        // A 0-/few-result response returns only the PRICE range facet, and
        // blindly rebuilding from that WIPED the drawer down to just Price —
        // the shopper couldn't see or clear the filters that produced the empty
        // result (QA 7/24, Dragana). augmentWithActive() adds synthetic facets
        // for any active selection BR omitted, so:
        //   - value facets present (real and/or synthetic) → render them
        //   - none at all (empty result, nothing selected)  → leave the drawer
        //     as-is (don't wipe the existing pills).
        if (drawer) {
          const augmented = augmentWithActive(facets, state.filters);
          const hasValueFacet = augmented.some(
            (f) =>
              f &&
              f.type !== "number_range" &&
              Array.isArray(f.value) &&
              f.value.some(
                (v) => v && v.name != null && String(v.name).trim() !== "",
              ),
          );
          if (hasValueFacet) {
            renderFacets(drawer, augmented, state.filters);
            // renderFacets rebuilds .bp-filter-drawer__body with a FRESH price
            // form (empty inputs), so an active price filter would look cleared
            // on reopen. Re-reflect the applied min/max after every rebuild
            // (QA 7/24: price 0–30 was wiped from the inputs when the drawer
            // re-rendered). Liquid never pre-fills these, so this is the only
            // thing that keeps the applied price visible.
            setPriceInputs(state.priceMin, state.priceMax);
          }
        }
        renderActiveFilters(root, state);
        updateLoadMore(loadMoreContainer, numFound, state);
      } catch (err) {
        console.error("[bp-bloomreach] fetch failed:", err);
        // Fail-soft: leave the Liquid-rendered grid untouched so the
        // page still works. Surface a console message and bail.
        return false;
      } finally {
        if (!background) setLoading(root, false);
        // Reveal the real grid + drop the skeleton after the first fetch
        // settles — BR results on success, or the untouched Shopify grid
        // on failure (fail-soft). Idempotent; later fetches keep it set.
        root.classList.add("bp-br-ready");
      }
      // A fresh (non-append) load renders only the first <=48 chunk; fill the
      // rest of the active page size (120 / All) in the background. Guarded to
      // fresh foreground loads so the cascade's own append fetches don't recurse.
      // Runs for every fresh fetch (initial, tab switch, filter/sort/clear).
      // `noCascade` is set by initialLoad()'s single-shot restore path, which
      // fetched the whole page count in one request and drives the tail itself.
      if (!append && !background && !(opts && opts.noCascade)) cascadeUntil(fillTarget(state));
      return true;
    }

    function updateLoadMore(container, numFound, st) {
      if (!container) return;
      // 0 results → no pagination at all; the empty-state message (setEmptyState)
      // owns that case. Previously this rendered an "End of results" pill that
      // looked like a button, highlighted on hover, and did nothing on click.
      if (numFound === 0) {
        container.innerHTML = "";
        return;
      }
      const loaded = st.start + st.rows;
      const more = loaded < numFound;
      container.innerHTML = more
        ? `<a class="bp-coll__loadmore" href="#" data-bp-br-loadmore>Load More</a>`
        : `<span class="bp-coll__loadmore is-end">End of results</span>`;
    }

    // "All": after the small first paint, load the rest in the
    // background — bigger chunks, yielding to the browser between each so
    // scrolling stays smooth and swatch hydration (lazy, per card) doesn't
    // stampede. Bounded by numFound so it can't loop forever.
    // Fill the grid up to `target` total items (capped by numFound), appending
    // in BG_CHUNK_ROWS (<=48) chunks. target = state.pageSize for a single tab
    // page (e.g. 120), page*pageSize for later Load-More pages, or Infinity for
    // "All". Bounded by numFound so it can't loop forever.
    async function cascadeUntil(target) {
      const countText = countEl ? countEl.textContent : "";
      const numFound = parseInt((countText.match(/\d+/) || [0])[0], 10) || 0;
      const cap = Math.min(numFound, target);

      // How many are already rendered (start+rows == running total after each
      // fetch, whether the last one was the first paint or a prior chunk).
      let loaded = state.start + state.rows;
      state.rows = BG_CHUNK_ROWS;

      const yieldToBrowser = () =>
        new Promise((resolve) => {
          if ("requestIdleCallback" in window) {
            window.requestIdleCallback(() => resolve(), { timeout: 500 });
          } else {
            setTimeout(resolve, 80);
          }
        });

      while (loaded < cap) {
        await yieldToBrowser(); // breathe between chunks
        state.start = loaded;
        state.rows = Math.min(BG_CHUNK_ROWS, cap - loaded); // never overshoot the target
        const ok = await fetchPage({ append: true, background: true });
        if (!ok) break;
        loaded += state.rows;
      }
      // Cascade finished — the grid is at full height for this page count. Force
      // a clamped restore in case the final grid is shorter than the saved offset
      // (e.g. fewer results than when the shopper left).
      maybeRestoreScroll(true);
    }

    // Total items the grid should hold after `state.page` pages of the active
    // per-page size. Infinity for "All".
    function fillTarget(st) {
      return st.pageSize === Infinity ? Infinity : st.page * st.pageSize;
    }

    // --- Wiring -------------------------------------------------------

    // Sort: re-fetch on change.
    if (sortSelect) {
      sortSelect.addEventListener("change", () => {
        state.sortBy = sortSelect.value;
        state.sort =
          SORT_MAP[sortSelect.value] != null ? SORT_MAP[sortSelect.value] : "";
        state.page = 1;
        state.start = 0;
        syncUrl(state);
        fetchPage();
      });
    }

    // Filter drawer: intercept Apply / Clear so we re-fetch instead of
    // navigating. The pill toggle behavior is handled by the existing
    // bp-collection JS (toggles `.is-active` locally); we only override
    // the commit step.
    if (drawer) {
      drawer.addEventListener(
        "click",
        (e) => {
          const apply = e.target.closest("[data-bp-filter-apply]");
          if (apply) {
            e.preventDefault();
            e.stopImmediatePropagation(); // beat the bp-collection handler
            state.filters = readActivePills(drawer);
            const priceForm = drawer.querySelector("[data-bp-price-form]");
            if (priceForm) {
              const fd = new FormData(priceForm);
              const min = fd.get("filter.v.price.gte");
              const max = fd.get("filter.v.price.lte");
              state.priceMin = min ? Number(min) : null;
              state.priceMax = max ? Number(max) : null;
            }
            state.page = 1;
            state.start = 0;
            syncUrl(state);
            fetchPage();
            // Close drawer the same way Liquid does.
            if (typeof drawer.close === "function" && drawer.open)
              drawer.close();
            return;
          }
          const clear = e.target.closest("[data-bp-filter-clear]");
          if (clear) {
            e.preventDefault();
            e.stopImmediatePropagation();
            state.filters = {};
            state.priceMin = null;
            state.priceMax = null;
            state.page = 1;
            state.start = 0;
            // Reset the drawer's own controls too, not just the state:
            // otherwise the pills stay highlighted and the price inputs
            // keep their values, so Clear looked like it did nothing.
            // (renderFacets rebuilds the pills from the now-empty filters,
            // but it PRESERVES the price form's current values — so wipe
            // them here, before the re-fetch captures them.)
            setPriceInputs(null, null);
            drawer
              .querySelectorAll("[data-bp-pill].is-active")
              .forEach((pill) => {
                pill.classList.remove("is-active");
                pill.removeAttribute("aria-current");
              });
            syncUrl(state);
            fetchPage();
            if (typeof drawer.close === "function" && drawer.open)
              drawer.close();
            return;
          }
        },
        true,
      ); // capture so we run first
    }

    // Applied-filter chips (above the grid) — remove one value, remove
    // the price filter, or clear all. Each re-fetches; the re-fetch
    // rebuilds the chips + the drawer pills from the new state.
    const activeWrap = root.querySelector("[data-active-filters]");
    if (activeWrap) {
      const wipePriceInputs = () => {
        const form = drawer && drawer.querySelector("[data-bp-price-form]");
        if (!form) return;
        form.querySelectorAll('input[type="number"]').forEach((i) => {
          i.value = "";
        });
      };
      activeWrap.addEventListener("click", (e) => {
        if (e.target.closest("[data-chip-clear]")) {
          state.filters = {};
          state.priceMin = null;
          state.priceMax = null;
          wipePriceInputs();
        } else if (e.target.closest("[data-chip-price]")) {
          state.priceMin = null;
          state.priceMax = null;
          wipePriceInputs();
        } else {
          const chip = e.target.closest("[data-chip-facet]");
          if (!chip) return;
          const facet = chip.dataset.chipFacet;
          const val = chip.dataset.chipValue;
          const vals = (state.filters[facet] || []).filter((v) => v !== val);
          if (vals.length) state.filters[facet] = vals;
          else delete state.filters[facet];
        }
        state.page = 1;
        state.start = 0;
        syncUrl(state);
        fetchPage();
      });
    }

    // Empty-state "Clear all filters" (setEmptyState) — same effect as the
    // active-filter chips' Clear all: wipe filters + price, then re-fetch.
    root.addEventListener("click", (e) => {
      if (!e.target.closest("[data-bp-br-empty-clear]")) return;
      e.preventDefault();
      state.filters = {};
      state.priceMin = null;
      state.priceMax = null;
      const priceForm = drawer && drawer.querySelector("[data-bp-price-form]");
      if (priceForm)
        priceForm.querySelectorAll('input[type="number"]').forEach((i) => {
          i.value = "";
        });
      state.page = 1;
      state.start = 0;
      syncUrl(state);
      fetchPage();
    });

    // Load More: append next page worth of items (where "page" = the
    // active per-page tab, not a hardcoded 48).
    if (loadMoreContainer) {
      loadMoreContainer.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-bp-br-loadmore]");
        if (!btn) return;
        e.preventDefault();
        state.page += 1;
        syncUrl(state, { replace: true });
        // Fill the next page-size worth in <=48-row chunks. cascadeUntil reads
        // the running total from start+rows, so this continues cleanly whether
        // the page size is 48 (one chunk) or 120 (several).
        cascadeUntil(fillTarget(state));
      });
    }

    // Per-page tabs: take over before the bp-collection cascadeUntil
    // handler (which would otherwise fetch Liquid HTML and mix
    // Shopify cards into the BR grid). Capture phase + stop so we
    // win the race.
    root.querySelectorAll("[data-perpage-value]").forEach((a) => {
      a.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          const raw = a.dataset.perpageValue; // '48' | '120' | '1000'
          const isAll = parseInt(raw, 10) >= 1000;
          state.pageSize = isAll ? Infinity : parseInt(raw, 10);
          state.rows = FIRST_PAINT_ROWS; // <=48 first-paint chunk
          state.page = 1;
          state.start = 0;
          state.perPageRaw = raw === "48" ? "" : raw;
          syncUrl(state);
          // Update active tab.
          root
            .querySelectorAll("[data-perpage-value]")
            .forEach((x) => x.classList.toggle("is-active", x === a));
          // Fetch the first chunk, then cascade-fill to the page size (48 needs
          // none; 120 fills to 120; All fills to numFound) in <=48-row chunks.
          fetchPage();
        },
        true,
      );
    });

    // Reflect the initial per-page selection (covers deep links into
    // ?per_page=120, page refreshes, etc.). With no ?per_page, fall back
    // to the section default (cfg.perPage) — not a hardcoded 48 — so the
    // right tab (e.g. All) highlights when that's the default.
    {
      const activeVal = state.perPageRaw || String(cfg.perPage || "48");
      root
        .querySelectorAll("[data-perpage-value]")
        .forEach((x) =>
          x.classList.toggle("is-active", x.dataset.perpageValue === activeVal),
        );
    }

    // Deep-page / Back-nav loads: build the grid in ONE big request instead of
    // the <=48 cascade, then restore scroll — so a Back from a PDP (URL carries
    // ?page=N, or a pending saved offset) lands at the saved position instantly
    // and precisely, with no "top, then scroll" as pages stream in. Fresh
    // page-1 loads keep the fast <=24 first paint + background cascade.
    function initialLoad() {
      const target = fillTarget(state); // page*pageSize, or Infinity for "All"
      const deep = pendingScrollY != null || state.page > 1;
      // Use single-shot fetch whenever we have a saved scroll position (pendingScrollY)
      // OR are on a deep page. The original guard `target > FIRST_PAINT_ROWS` skipped
      // this for page-1 restores (target=48 === FIRST_PAINT_ROWS), leaving the regular
      // cascade path in charge of the restore — which races against iOS's page transition
      // animation and loses. Fetching the full page count in one shot gives the grid
      // its final height before we attempt scrollTo(), eliminating the race.
      if (deep && (target === Infinity || target >= FIRST_PAINT_ROWS)) {
        const shot =
          target === Infinity ? RESTORE_MAX_ROWS : Math.min(target, RESTORE_MAX_ROWS);
        state.start = 0;
        state.rows = shot;
        // One request → grid at (near) full height in a single round-trip.
        fetchPage({ noCascade: true }).then((ok) => {
          if (ok === false) return; // fetch failed → Liquid grid stays (fail-soft)
          const loaded = state.start + state.rows; // == shot
          if (target === Infinity || loaded < target) {
            // Deeper than one shot can hold (All, or a very deep Load-More
            // page): fill the remainder. The saved offset is almost always
            // within the shot, so the restore already fired; the tail just
            // grows the grid below it. cascadeUntil() calls maybeRestoreScroll(true).
            cascadeUntil(target);
          } else {
            // Whole page count arrived in the single shot → settle + release.
            maybeRestoreScroll(true);
          }
        });
      } else {
        fetchPage(); // fast <=48 first paint + background cascade
      }
    }

    // Back/forward: re-hydrate from URL and refetch.
    window.addEventListener("popstate", () => {
      state = hydrateFromUrl(perPage);
      state.catId = cfg.catId;
      state.searchQuery = cfg.searchQuery || "";
      if (sortSelect && state.sortBy) sortSelect.value = state.sortBy;
      initialLoad();
    });

    // Initial fetch — replace Liquid grid with BR data.
    initialLoad();
  }

  // Read currently staged pills (`.is-active`) and group by BR facet
  // field.
  //
  // Resolution order matters. `data-bp-facet` carries the BR field
  // verbatim and is authoritative. Only pills rendered by an older cache
  // (or by the Liquid fallback drawer) lack it, so we then try the
  // Shopify key map, then the `filter.br.` prefix.
  //
  // This used to be `FILTER_MAP[shopifyKey]` with `if (!facet) return`,
  // which silently DROPPED every facet outside colors/sizes/brand — so
  // CATEGORY (and any newly added facet) rendered pills, accepted
  // clicks, and then filtered nothing on Apply.
  function facetFieldForPill(pill) {
    if (pill.dataset.bpFacet) return pill.dataset.bpFacet;
    const key = pill.dataset.param || "";
    if (FILTER_MAP[key]) return FILTER_MAP[key];
    if (key.indexOf(BR_PARAM_PREFIX) === 0)
      return key.slice(BR_PARAM_PREFIX.length);
    return null;
  }

  // BR's colour facet is case-sensitive and stored lowercase — "Black" in the
  // query/URL returns 0 results while "black" works. Normalize colour values to
  // lowercase wherever they ENTER state (URL parse + active pills) so the shared
  // URL reads ?filter.v.option.color=black and a reload matches. Other facets
  // (e.g. sizes "M"/"L") keep their original casing.
  function normFacetValue(facet, value) {
    return facet === "colors" ? String(value == null ? "" : value).toLowerCase() : value;
  }
  // Title-case a normalized colour for the removable chip's LABEL only (the
  // data-chip-value stays lowercase so removal still matches state).
  function displayFacetValue(facet, value) {
    return facet === "colors"
      ? String(value).replace(/\b\w/g, function (c) { return c.toUpperCase(); })
      : value;
  }

  function readActivePills(drawer) {
    const out = {};
    drawer.querySelectorAll("[data-bp-pill].is-active").forEach((pill) => {
      const val = pill.dataset.value;
      if (!val) return;
      const facet = facetFieldForPill(pill);
      if (!facet) return;
      if (!out[facet]) out[facet] = [];
      const nv = normFacetValue(facet, val);
      if (!out[facet].includes(nv)) out[facet].push(nv);
    });
    return out;
  }

  window.BpBloomreach = { init };
})();
